import React, { useState, useEffect, useMemo } from "react";
import { apiRequest, setAuthErrorHandler } from "./apiClient";

declare global {
  interface Window {
    google?: any;
  }
}

// --- TIPI ---
type View = "landing" | "login" | "dashboard" | "tablet";
type DashboardTab =
  | "classe"
  | "competizioni"
  | "impostazioni"
  | "validazioni"
  | "gestione-classi"
  | "gestione-missioni"
  | "gestione-competizioni"
  | "gestione-utenti"
  | "storico";
type UserRole =
  | "esterno"
  | "partecipante"
  | "esaminatore"
  | "amministratore"
  | "tablet";

interface User {
  email: string;
  role: UserRole;
  name: string;
  class?: string;
}

interface Action {
  id: string;
  classId: string;
  className: string;
  imageUrl: string;
  timestamp: Date;
  status: "pending" | "approved" | "partial" | "rejected";
  points: number;
  evaluatedBy?: string;
  description?: string;
  senderName?: string;
  competitionName?: string;
  missionType?: string;
}

interface Competition {
  id: string;
  name: string;
  description: string;
  startDate: Date;
  endDate: Date;
  classes: string[];
  classIds: string[];
  missionTypes: string[];
  status: "active" | "upcoming" | "archived";
  school: string;
  schoolYear: string;
  ranking?: Array<{
    position: number;
    classId: string;
    className: string;
    score: number;
  }>;
}

interface LoginApiResponse {
  token: string;
  user: User;
}

interface PendingActionsResponse {
  actions: Array<
    Omit<Action, "timestamp" | "imageUrl"> & {
      timestamp: string;
      imageUrl?: string;
      image_url?: string;
    }
  >;
}

interface ClassSummaryResponse {
  class: {
    id: string;
    name: string;
    schoolYear: string;
    location: string;
    totalPoints: number;
    completedCount: number;
    pendingCount: number;
    rejectedCount: number;
  };
}

interface ClassStudentsResponse {
  students: Array<{
    id: string;
    name: string;
    email: string;
  }>;
}

interface ClassRankingResponse {
  ranking: Array<{
    position: number;
    classId: string;
    className: string;
    score: number;
  }>;
}

interface ActionsHistoryResponse {
  actions: Array<{
    id: string;
    missionType: string;
    status: "pending" | "approved" | "partial" | "rejected";
    points: number;
    timestamp: string;
    competitionName: string;
  }>;
}

interface UsersListResponse {
  users: Array<{
    id: string;
    name: string;
    email: string;
    role: string;
  }>;
}

interface ClassesListResponse {
  classes: Array<{
    id: string;
    name: string;
    number?: number;
    section?: string;
    school_year: string;
    location: string;
    student_count?: number;
  }>;
}

interface ClassEmailsResponse {
  emails: string[];
}

interface MissionDefinition {
  id: string;
  name: string;
  description: string;
  imageUrl: string;
  points: number;
}

interface CompetitionsListResponse {
  competitions: Array<{
    id: string;
    name: string;
    description?: string;
    startDate: string;
    endDate: string;
    classes: string[];
    classIds?: string[];
    missionTypes?: string[];
    status: "active" | "upcoming" | "archived";
    school?: string;
    schoolYear?: string;
  }>;
}

interface MenuItem {
  id: DashboardTab;
  label: string;
  icon: string;
}

const isPrivilegedRole = (role: UserRole) =>
  role === "amministratore" || role === "esaminatore" || role === "tablet";

const mapRoleToKnownValue = (role: string): UserRole => {
  const normalized = role.trim().toLowerCase();

  switch (normalized) {
    case "partecipante":
    case "studente":
      return "partecipante";
    case "esaminatore":
    case "exam":
      return "esaminatore";
    case "amministratore":
    case "admin":
      return "amministratore";
    case "tablet":
      return "tablet";
    case "esterno":
    default:
      return "esterno";
  }
};

const normalizeUserRole = (user: User): User => {
  const mappedRole = mapRoleToKnownValue(String(user.role ?? ""));
  const mappedUser: User = {
    ...user,
    role: mappedRole,
  };

  if (isPrivilegedRole(mappedRole)) {
    return mappedUser;
  }

  const className = mappedUser.class?.trim() ?? "";
  if (className.length === 0) {
    return {
      ...mappedUser,
      role: "esterno",
    };
  }

  return mappedUser;
};

const sanitizeMissionDescription = (description: string): string =>
  description.replace(/\s*\n?\s*Tipo competizione:\s*.*$/i, "").trim();

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
        return;
      }
      reject(new Error("Impossibile leggere il file selezionato."));
    };
    reader.onerror = () =>
      reject(new Error("Impossibile leggere il file selezionato."));
    reader.readAsDataURL(file);
  });

const toInputDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseEmailsFromText = (value: string): string[] => {
  const raw = value
    .split(/[\n,;\s]+/)
    .map((email) => email.trim().toLowerCase())
    .filter((email) => email.length > 0);

  const unique = Array.from(new Set(raw));
  return unique.filter((email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
};

const parseEmailsFromJsonPayload = (payload: unknown): string[] => {
  const source = Array.isArray(payload)
    ? payload
    : typeof payload === "object" && payload !== null
      ? ((payload as { students?: unknown; emails?: unknown }).students ??
        (payload as { students?: unknown; emails?: unknown }).emails)
      : [];

  if (!Array.isArray(source)) {
    return [];
  }

  const raw = source
    .map((item) => {
      if (typeof item === "string") {
        return item;
      }
      if (typeof item === "object" && item !== null && "email" in item) {
        return String((item as { email: unknown }).email ?? "");
      }
      return "";
    })
    .map((email) => email.trim().toLowerCase())
    .filter((email) => email.length > 0);

  const unique = Array.from(new Set(raw));
  return unique.filter((email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
};

const getEffectiveUserRole = (
  user: User,
  competitions: Competition[],
): UserRole => {
  if (isPrivilegedRole(user.role)) {
    return user.role;
  }

  const className = user.class?.trim() ?? "";
  const hasClass = className.length > 0;
  const isInCompetition =
    hasClass &&
    competitions.some((competition) => competition.classes.includes(className));

  if (!hasClass && !isInCompetition) {
    return "esterno";
  }

  return user.role;
};

const resolveApiImageUrl = (value: string): string => {
  const raw = value.trim();
  if (!raw) {
    return "";
  }

  if (/^(data:|blob:|https?:\/\/)/i.test(raw)) {
    return raw;
  }

  if (raw.startsWith("//")) {
    return `${window.location.protocol}${raw}`;
  }

  const apiBase = (
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api"
  ).replace(/\/$/, "");
  const apiUrl = new URL(apiBase || "/api", window.location.origin);

  if (raw.startsWith("/")) {
    return `${apiUrl.origin}${raw}`;
  }

  return `${apiUrl.origin}/${raw.replace(/^\/+/, "")}`;
};

const getMenuItemsForRole = (role: UserRole): MenuItem[] => {
  const roleItems: Record<UserRole, MenuItem[]> = {
    esterno: [
      { id: "classe", label: "La mia classe", icon: "🏢" },
      { id: "competizioni", label: "Altre competizioni", icon: "📊" },
    ],
    partecipante: [
      { id: "classe", label: "La mia classe", icon: "🏢" },
      { id: "competizioni", label: "Altre competizioni", icon: "📊" },
    ],
    esaminatore: [
      { id: "competizioni", label: "Altre competizioni", icon: "📊" },
      { id: "validazioni", label: "Missioni da validare", icon: "✓" },
    ],
    amministratore: [
      { id: "competizioni", label: "Altre competizioni", icon: "📊" },
      { id: "validazioni", label: "Missioni da validare", icon: "✓" },
      { id: "gestione-classi", label: "Gestione classi", icon: "🏫" },
      { id: "gestione-missioni", label: "Gestione missioni", icon: "🎯" },
      {
        id: "gestione-competizioni",
        label: "Gestione competizioni",
        icon: "👑",
      },
      { id: "gestione-utenti", label: "Gestione utenti", icon: "👥" },
    ],
    tablet: [{ id: "competizioni", label: "Altre competizioni", icon: "📊" }],
  };

  return roleItems[role] ?? [];
};

// --- COMPONENTI UI UNIVERSALI ---
const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = "",
}) => (
  <div
    className={`bg-white rounded-3xl shadow-sm border border-stone-200 p-6 ${className}`}
  >
    {children}
  </div>
);

interface StyledFilterDropdownOption {
  value: string;
  label: string;
}

const StyledFilterDropdown: React.FC<{
  value: string;
  options: StyledFilterDropdownOption[];
  onChange: (value: string) => void;
  className?: string;
  disabled?: boolean;
}> = ({ value, options, onChange, className = "", disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const selectedLabel =
    options.find((option) => option.value === value)?.label ??
    options[0]?.label ??
    "";

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen((current) => !current)}
        className="w-full bg-stone-50 border-2 border-stone-200 rounded-xl px-4 py-3 pr-11 font-semibold text-slate-700 shadow-sm transition focus:outline-none focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100 hover:border-stone-300 text-left disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {selectedLabel}
      </button>
      <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-stone-500">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className={`h-5 w-5 transition-transform ${isOpen ? "rotate-180" : ""}`}
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.168l3.71-3.938a.75.75 0 1 1 1.08 1.04l-4.25 4.51a.75.75 0 0 1-1.08 0l-4.25-4.51a.75.75 0 0 1 .02-1.06Z"
            clipRule="evenodd"
          />
        </svg>
      </span>
      {isOpen && !disabled && (
        <div className="absolute z-20 mt-2 w-full bg-white border-2 border-stone-200 rounded-xl shadow-lg overflow-hidden">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={`w-full px-4 py-3 text-left font-semibold transition ${value === option.value ? "bg-emerald-50 text-emerald-700" : "text-slate-700 hover:bg-stone-50"}`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// --- VISTA: LANDING ---
const LandingPage: React.FC<{
  onLoginClick: () => void;
  user: User | null;
  onOpenDashboard: (tab?: DashboardTab) => void;
  onLogout: () => void;
}> = ({ onLoginClick, user, onOpenDashboard, onLogout }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [currentImg, setCurrentImg] = useState(0);
  const userRole = user ? mapRoleToKnownValue(String(user.role ?? "")) : null;
  const availableMenuItems = userRole ? getMenuItemsForRole(userRole) : [];

  const images = [
    "https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?auto=format&fit=crop&q=80&w=800",
    "https://images.unsplash.com/photo-1501854140801-50d01698950b?auto=format&fit=crop&q=80&w=800",
    "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&q=80&w=800",
  ];

  const nextImg = () => setCurrentImg((prev) => (prev + 1) % images.length);
  const prevImg = () =>
    setCurrentImg((prev) => (prev - 1 + images.length) % images.length);

  // Auto-scorrimento ogni 4 secondi
  useEffect(() => {
    const timer = setInterval(() => {
      nextImg();
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-[100dvh] bg-stone-50 overflow-x-hidden">
      {/* Overlay per Sidebar */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm z-[60] transition-opacity duration-300"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {isGuideOpen && (
        <div className="fixed inset-0 z-[90] bg-black/40 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
          <div className="relative w-full max-w-5xl rounded-[2rem] bg-white shadow-2xl border border-stone-200 overflow-hidden">
            <div className="flex items-center justify-between gap-4 px-8 py-6 bg-emerald-600">
              <div>
                <h2 className="text-2xl font-black text-white tracking-tight">
                  Guida GreenHero
                </h2>
                <p className="text-sm text-emerald-100 mt-1">
                  Tutte le informazioni utili in un’unica pagina.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsGuideOpen(false)}
                className="text-white/90 hover:text-white rounded-full p-2 bg-emerald-700/90 transition"
              >
                Chiudi
              </button>
            </div>
            <div className="max-h-[80vh] overflow-y-auto px-8 py-8 bg-stone-50">
              <section className="space-y-4">
                <p className="text-slate-700 text-base leading-relaxed">
                  Green Hero è una piattaforma web finalizzata
                  all’incentivazione di comportamenti sostenibili in ambito
                  scolastico. Il sistema applica dinamiche di gamification alle
                  azioni ecologiche per innescare una competizione tra le
                  diverse classi dell'istituto.
                </p>
                <div className="grid gap-6 sm:grid-cols-2">
                  <article className="rounded-3xl border border-stone-200 bg-white p-5 shadow-sm">
                    <h3 className="text-sm font-black uppercase tracking-[0.2em] text-emerald-600 mb-3">
                      COME UTILIZZARLA
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Il funzionamento del sistema si articola attraverso
                      un’interfaccia web divisa in 2 parti: utenti e tablet.
                      L’accesso avviene tramite Single Sign-On istituzionale, e
                      il sistema abilita dinamicamente le funzionalità in base
                      al ruolo.
                    </p>
                  </article>
                  <article className="rounded-3xl border border-stone-200 bg-white p-5 shadow-sm">
                    <h3 className="text-sm font-black uppercase tracking-[0.2em] text-emerald-600 mb-3">
                      ACCESSO E RUOLI
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      L’accesso è consentito esclusivamente tramite email
                      istituzionale con Google. Il profilo utente viene
                      riconosciuto automaticamente e determina quali pagine sono
                      visibili.
                    </p>
                  </article>
                </div>
              </section>

              <section className="mt-10 space-y-4">
                <h3 className="text-xl font-black text-slate-900">
                  Descrizione delle sezioni principali
                </h3>
                <div className="space-y-4">
                  <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm">
                    <h4 className="text-base font-bold text-slate-900 mb-2">
                      La Mia Classe
                    </h4>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Area dedicata al monitoraggio del proprio gruppo. Include
                      Competizione, Punteggi e Classifica. In Competizione
                      vengono mostrati i dati delle missioni svolte; in Punteggi
                      il totale dei punti e lo stato delle azioni; in Classifica
                      il posizionamento rispetto alle altre classi.
                    </p>
                  </div>
                  <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm">
                    <h4 className="text-base font-bold text-slate-900 mb-2">
                      Altre Competizioni
                    </h4>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Portale di consultazione per visualizzare classifiche
                      attuali e passate relative ad altri concorsi gestiti dalla
                      piattaforma. Sono presenti filtri per classe, anno e
                      scuola con card cliccabili per i dettagli.
                    </p>
                  </div>
                  <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm">
                    <h4 className="text-base font-bold text-slate-900 mb-2">
                      Missioni da Validare
                    </h4>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Dashboard operativa dove vengono visualizzate le azioni in
                      attesa di approvazione. Ogni voce mostra immagine,
                      descrizione, mittente, data, classe e tipo di missione.
                    </p>
                  </div>
                </div>
              </section>

              <section className="mt-10 space-y-4">
                <h3 className="text-xl font-black text-slate-900">
                  Ruoli utente
                </h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm">
                    <h4 className="font-bold text-slate-900">Utente Esterno</h4>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Visualizza La Mia Classe e Altre Competizioni. Mostra un
                      avviso se non partecipa a una competizione attiva.
                    </p>
                  </div>
                  <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm">
                    <h4 className="font-bold text-slate-900">
                      Utente Partecipante
                    </h4>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Ha pieno accesso ai dati della classe e alle classifiche
                      della competizione in cui è inserito.
                    </p>
                  </div>
                  <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm">
                    <h4 className="font-bold text-slate-900">
                      Utente Esaminatore
                    </h4>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Visualizza Altre Competizioni e Missioni da Validare per
                      la revisione delle prove caricate.
                    </p>
                  </div>
                  <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm">
                    <h4 className="font-bold text-slate-900">
                      Utente Amministratore
                    </h4>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Visualizza tutte le voci: Altre Competizioni, Missioni da
                      Validare, Gestione Competizioni, Gestione Utenti e
                      Gestione Classi.
                    </p>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar Mobile/Landing */}
      <div
        className={`fixed top-0 left-0 h-full w-80 bg-white z-[70] shadow-2xl transition-transform duration-300 ease-in-out transform ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"} p-8 border-r border-stone-200 flex flex-col`}
      >
        <div className="flex justify-between items-center mb-6">
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="text-2xl font-black text-emerald-600 tracking-tighter flex items-center gap-2 cursor-pointer"
          >
            <span className="text-3xl">🌿</span> GreenHero
          </button>
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="p-2 hover:bg-stone-100 rounded-full text-stone-400"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {user ? (
          <div className="flex-1 min-h-0 flex flex-col pb-6">
            <div className="space-y-2">
              <div className="text-[10px] font-black text-stone-300 uppercase tracking-[0.25em] pl-2 pb-1">
                DASHBOARD
              </div>
              {availableMenuItems.map((item) => (
                <button
                  key={`landing-menu-${item.id}`}
                  onClick={() => {
                    setIsSidebarOpen(false);
                    onOpenDashboard(item.id);
                  }}
                  className="w-full p-3 rounded-2xl font-black text-left flex items-center gap-4 transition text-stone-500 bg-stone-50 hover:bg-emerald-50 hover:text-emerald-700"
                >
                  <span className="text-xl">{item.icon}</span> {item.label}
                </button>
              ))}
            </div>

            <div className="mt-auto pt-4 pb-2 border-t border-stone-100 space-y-2">
              <button
                onClick={() => {
                  setIsSidebarOpen(false);
                  onOpenDashboard("impostazioni");
                }}
                className="w-full p-3 rounded-2xl font-black text-left flex items-center gap-4 transition text-stone-500 bg-stone-50 hover:bg-emerald-50 hover:text-emerald-700"
              >
                <span className="text-xl">⚙️</span> Impostazioni
              </button>
              <button
                onClick={() => {
                  setIsSidebarOpen(false);
                  onLogout();
                }}
                className="w-full p-3 rounded-2xl font-black text-left flex items-center gap-4 transition text-red-500 bg-red-50/60 hover:bg-red-50"
              >
                <span className="text-xl">🚪</span> Logout
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full">
            <div className="text-center mb-8">
              <p className="text-stone-400 font-bold text-sm mb-2">
                Accedi per visualizzare il menu
              </p>
            </div>
            <button
              onClick={onLoginClick}
              className="w-full bg-emerald-600 text-white px-6 py-4 rounded-xl font-bold hover:bg-emerald-700 transition shadow-lg"
            >
              Vai al Login
            </button>
          </div>
        )}
      </div>

      <nav className="flex justify-between items-center px-6 py-4 max-w-7xl mx-auto relative z-50">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="p-2 bg-stone-200 rounded-xl hover:bg-stone-300 transition text-stone-700"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="text-2xl font-black text-emerald-600 flex items-center gap-2 tracking-tighter hover:opacity-80 transition"
          >
            🌿 GreenHero
          </button>
        </div>
        <div className="flex gap-4 items-center">
          {!user && (
            <button
              onClick={onLoginClick}
              className="bg-emerald-600 text-white px-6 py-2 rounded-xl font-bold hover:bg-emerald-700 transition shadow-lg shadow-emerald-100"
            >
              ACCEDI
            </button>
          )}
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-20 flex flex-col md:flex-row items-center gap-12">
        <div className="md:w-1/2 text-center md:text-left">
          <h1 className="text-5xl md:text-7xl font-black text-slate-900 leading-tight mb-6 uppercase tracking-tighter">
            Diventa il <span className="text-emerald-600">GreenHero</span> che
            il mondo aspetta.
          </h1>
          <p className="text-xl text-slate-500 mb-10 font-medium">
            Partecipa alle sfide della tua scuola, accumula punti e salva il
            pianeta insieme alla tua classe.
          </p>
          <div className="w-full h-80 bg-stone-200 rounded-[2.5rem] overflow-hidden relative shadow-2xl border-4 border-white group/slider mx-auto md:mx-0">
            <div className="relative w-full h-full">
              {images.map((img, i) => (
                <img
                  key={i}
                  src={img}
                  className={`absolute inset-0 w-full h-full object-cover transition-all duration-1000 ease-in-out ${
                    i === currentImg
                      ? "opacity-100 scale-100"
                      : "opacity-0 scale-110"
                  }`}
                  alt={`Ambiente ${i}`}
                />
              ))}
            </div>

            {/* Pulsanti Navigazione più eleganti e meno invasivi */}
            <div className="absolute inset-0 flex items-center justify-between px-4 pointer-events-none">
              <button
                onClick={prevImg}
                className="bg-white/10 backdrop-blur-md p-2 rounded-full text-white border border-white/20 pointer-events-auto hover:bg-white hover:text-emerald-600 transition-all duration-300 opacity-0 group-hover/slider:opacity-100 transform -translate-x-4 group-hover/slider:translate-x-0 active:scale-90"
                aria-label="Immagine precedente"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.5"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <button
                onClick={nextImg}
                className="bg-white/10 backdrop-blur-md p-2 rounded-full text-white border border-white/20 pointer-events-auto hover:bg-white hover:text-emerald-600 transition-all duration-300 opacity-0 group-hover/slider:opacity-100 transform translate-x-4 group-hover/slider:translate-x-0 active:scale-90"
                aria-label="Immagine successiva"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.5"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>

            {/* Indicatori slider */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 pointer-events-none">
              {images.map((_, i) => (
                <div
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full transition-all duration-500 ${i === currentImg ? "bg-white w-4" : "bg-white/30"}`}
                />
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* Footer Centrato e Compatto */}
      <footer className="max-w-7xl mx-auto px-6 py-10 border-t border-stone-200 flex flex-col items-center text-center gap-8">
        <div className="grid grid-cols-2 gap-x-12 md:gap-x-20 gap-y-6">
          <div className="flex flex-col gap-2">
            <span className="text-stone-900 font-black uppercase tracking-[0.25em] text-[10px]">
              Risorse
            </span>
            <a
              href="https://www.iisvittorioveneto.edu.it/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-stone-400 font-bold text-xs hover:text-emerald-600 transition decoration-emerald-200 hover:underline underline-offset-4 decoration-2"
            >
              SITO SCUOLA
            </a>
            <button
              type="button"
              onClick={() => setIsGuideOpen(true)}
              className="text-left text-stone-400 font-bold text-xs hover:text-emerald-600 transition decoration-emerald-200 hover:underline underline-offset-4 decoration-2"
            >
              GUIDA
            </button>
            <a
              href="mailto:stefano.manighetti@iisvittorioveneto.it"
              className="text-left text-stone-400 font-bold text-xs hover:text-emerald-600 transition decoration-emerald-200 hover:underline underline-offset-4 decoration-2"
            >
              SUPPORTO
            </a>
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-stone-900 font-black uppercase tracking-[0.25em] text-[10px]">
              Social
            </span>
            <a
              href="https://www.instagram.com/stefanomanighetti/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-stone-400 font-bold text-xs hover:text-emerald-600 transition decoration-emerald-200 hover:underline underline-offset-4 decoration-2"
            >
              ABOUT US
            </a>
          </div>
        </div>

        <div className="flex flex-col items-center gap-4">
          <div className="text-3xl font-black text-emerald-600/10 select-none tracking-tighter transition hover:text-emerald-600/20">
            GreenHero
          </div>
          <div className="max-w-md">
            <div className="font-bold text-stone-300 uppercase tracking-widest text-[9px] leading-relaxed">
              COPYRIGHT © 2024 - TUTTI I DIRITTI RISERVATI
              <br />
              DIVENTA IL CAMBIAMENTO CHE VUOI VEDERE NEL MONDO
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// --- VISTA: LOGIN ---
const LoginPage: React.FC<{
  onBack: () => void;
  onLogin: (user: User, token?: string) => void;
}> = ({ onBack, onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isGoogleReady, setIsGoogleReady] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const googleButtonContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [googleClientId, setGoogleClientId] = useState(
    (
      (import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined) ?? ""
    ).trim(),
  );

  const fetchGoogleClientIdFromBackend = async (): Promise<string> => {
    const data = await apiRequest<{ google_client_id?: string }>(
      "config.public",
    );
    return (data.google_client_id ?? "").trim();
  };

  useEffect(() => {
    if (googleClientId) {
      return;
    }

    const loadPublicConfig = async () => {
      try {
        const resolvedClientId = await fetchGoogleClientIdFromBackend();
        if (resolvedClientId) {
          setGoogleClientId(resolvedClientId);
        }
      } catch {
        // Ignore: fallback message is shown when user presses Google login.
      }
    };

    loadPublicConfig();
  }, [googleClientId]);

  const ensureGoogleScript = async () => {
    if (window.google?.accounts?.id) {
      return;
    }

    await new Promise<void>((resolve, reject) => {
      const existing = document.querySelector(
        'script[data-google-gsi="true"]',
      ) as HTMLScriptElement | null;

      if (existing) {
        existing.addEventListener("load", () => resolve(), { once: true });
        existing.addEventListener(
          "error",
          () => reject(new Error("Impossibile caricare Google Sign-In.")),
          {
            once: true,
          },
        );
        return;
      }

      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.dataset.googleGsi = "true";
      script.onload = () => resolve();
      script.onerror = () =>
        reject(new Error("Impossibile caricare Google Sign-In."));
      document.head.appendChild(script);
    });
  };

  const setupGoogleWithClientId = async (clientId: string) => {
    await ensureGoogleScript();
    if (!window.google?.accounts?.id) {
      throw new Error("Google Sign-In non disponibile.");
    }

    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: async (response: { credential?: string }) => {
        const idToken = (response?.credential ?? "").trim();
        if (!idToken) {
          setLoginError("Token Google non valido.");
          return;
        }

        setLoginError(null);
        setIsLoggingIn(true);
        try {
          const data = await apiRequest<LoginApiResponse>("login.google", {
            method: "POST",
            body: {
              id_token: idToken,
            },
          });
          onLogin(data.user, data.token);
        } catch (error) {
          setLoginError(
            error instanceof Error ? error.message : "Accesso Google fallito",
          );
        } finally {
          setIsLoggingIn(false);
        }
      },
    });

    if (googleButtonContainerRef.current) {
      googleButtonContainerRef.current.innerHTML = "";
      window.google.accounts.id.renderButton(googleButtonContainerRef.current, {
        theme: "outline",
        size: "large",
        text: "signin_with",
        shape: "rectangular",
        width: 340,
      });
    }

    setIsGoogleReady(true);
  };

  useEffect(() => {
    let cancelled = false;

    const setupGoogle = async () => {
      if (!googleClientId) {
        setIsGoogleReady(false);
        return;
      }

      try {
        await setupGoogleWithClientId(googleClientId);
        if (cancelled) {
          return;
        }
      } catch (error) {
        if (!cancelled) {
          setIsGoogleReady(false);
          setLoginError(
            error instanceof Error
              ? error.message
              : "Configurazione Google non disponibile.",
          );
        }
      }
    };

    setupGoogle();

    return () => {
      cancelled = true;
    };
  }, [googleClientId, onLogin]);

  const handleGoogleLogin = async () => {
    let resolvedClientId = googleClientId;

    if (!resolvedClientId) {
      try {
        resolvedClientId = await fetchGoogleClientIdFromBackend();
        if (resolvedClientId) {
          setGoogleClientId(resolvedClientId);
          await setupGoogleWithClientId(resolvedClientId);
        }
      } catch {
        // handled below via message
      }
    }

    if (!resolvedClientId) {
      setLoginError(
        "Configura Google Client ID (VITE_GOOGLE_CLIENT_ID nel frontend o GOOGLE_CLIENT_ID nel backend).",
      );
      return;
    }

    if (!window.google?.accounts?.id || !isGoogleReady) {
      try {
        await setupGoogleWithClientId(resolvedClientId);
      } catch {
        setLoginError("Google Sign-In non è pronto. Riprova tra un attimo.");
        return;
      }
    }

    setLoginError(null);
    const renderedButton = googleButtonContainerRef.current?.querySelector(
      'div[role="button"], button',
    ) as HTMLElement | null;

    if (renderedButton) {
      renderedButton.click();
      return;
    }

    window.google.accounts.id.prompt();
  };

  const handleLogin = async () => {
    setLoginError(null);
    setIsLoggingIn(true);

    try {
      const loginEmail = email.trim();
      const loginPassword = password;
      let passwordHash = "";

      if (globalThis.crypto?.subtle) {
        const passwordToHash = new TextEncoder().encode(loginPassword);
        const digestBuffer = await crypto.subtle.digest(
          "SHA-256",
          passwordToHash,
        );
        passwordHash = Array.from(new Uint8Array(digestBuffer))
          .map((byte) => byte.toString(16).padStart(2, "0"))
          .join("");
      }

      const data = await apiRequest<LoginApiResponse>("login", {
        method: "POST",
        body: {
          email: loginEmail,
          password: loginPassword,
          ...(passwordHash ? { password_hash: passwordHash } : {}),
        },
      });

      onLogin(data.user, data.token);
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : "Login fallito");
    } finally {
      setIsLoggingIn(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-stone-100 flex flex-col items-center justify-center p-4">
      <button
        onClick={onBack}
        className="mb-6 text-2xl font-black text-emerald-600 tracking-tighter hover:opacity-80 transition cursor-pointer"
      >
        🌿 GreenHero
      </button>

      <div className="w-full max-w-md">
        {/* Login Normale */}
        <Card className="p-7 border-2">
          <h2 className="text-2xl font-black mb-6 text-center text-slate-800">
            Accedi
          </h2>
          <div className="space-y-5">
            <div>
              <label
                htmlFor="login-email"
                className="block text-xs font-black text-stone-400 mb-2 uppercase tracking-widest"
              >
                email
              </label>
              <div className="relative">
                <input
                  id="login-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder="nome.cognome@scuola.it"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5 focus:ring-2 ring-emerald-500 outline-none transition font-medium"
                />
                <span className="absolute right-4 top-3 text-stone-300 font-bold">
                  @
                </span>
              </div>
            </div>
            <div>
              <label
                htmlFor="login-password"
                className="block text-xs font-black text-stone-400 mb-2 uppercase tracking-widest"
              >
                password
              </label>
              <div className="relative">
                <input
                  id="login-password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                  className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5 focus:ring-2 ring-emerald-500 outline-none transition font-medium"
                />
              </div>
            </div>
            <button
              disabled={isLoggingIn}
              onClick={() => handleLogin()}
              className="w-full bg-emerald-600 text-white py-3 rounded-xl font-black text-base shadow-xl shadow-emerald-100 hover:bg-emerald-700 transition transform hover:-translate-y-1 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isLoggingIn ? "Accesso in corso..." : "Login"}
            </button>

            <div className="space-y-3">
              <button
                type="button"
                disabled={isLoggingIn}
                onClick={handleGoogleLogin}
                className="w-full bg-white border-2 border-stone-200 text-slate-700 py-3 rounded-xl font-black text-sm hover:bg-stone-50 transition disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Accedi con Google
              </button>
              <div ref={googleButtonContainerRef} className="hidden" />
            </div>

            {loginError && (
              <div className="text-sm font-bold text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                {loginError}
              </div>
            )}
          </div>
        </Card>
      </div>

      <button
        onClick={onBack}
        className="mt-6 text-stone-400 font-black hover:text-emerald-600 transition uppercase text-xs tracking-[0.2em]"
      >
        ← Torna alla home
      </button>
    </div>
  );
};

// --- VISTA: TABLET ---
const TabletInterface: React.FC<{
  onLogout: () => void;
  token: string | null;
}> = ({ onLogout, token }) => {
  const [currentStep, setCurrentStep] = useState<"class" | "mission" | "photo">(
    "class",
  );
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedMission, setSelectedMission] = useState("");
  const [capturedAt, setCapturedAt] = useState<Date | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const tapCountRef = React.useRef(0);
  const tapTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [classes, setClasses] = useState<string[]>([]);
  const [missionOptions, setMissionOptions] = useState<string[]>([]);
  const [isLoadingSetup, setIsLoadingSetup] = useState(false);
  const [setupError, setSetupError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setClasses([]);
      setMissionOptions([]);
      setSetupError("Sessione non valida. Effettua di nuovo il login.");
      return;
    }

    const loadTabletSetup = async () => {
      setIsLoadingSetup(true);
      setSetupError(null);

      try {
        const data = await apiRequest<CompetitionsListResponse>(
          "competitions.list",
          {
            token,
          },
        );

        const activeCompetitions = data.competitions.filter(
          (competition) => competition.status === "active",
        );

        setClasses(
          Array.from(
            new Set(
              activeCompetitions.flatMap(
                (competition) => competition.classes ?? [],
              ),
            ),
          ),
        );
        setMissionOptions(
          Array.from(
            new Set(
              activeCompetitions.flatMap(
                (competition) => competition.missionTypes ?? [],
              ),
            ),
          ),
        );
      } catch (error) {
        setClasses([]);
        setMissionOptions([]);
        setSetupError(
          error instanceof Error
            ? error.message
            : "Errore caricamento classi e missioni",
        );
      } finally {
        setIsLoadingSetup(false);
      }
    };

    loadTabletSetup();
  }, [token]);

  const handleHiddenTap = () => {
    tapCountRef.current += 1;
    if (tapTimerRef.current) clearTimeout(tapTimerRef.current);
    tapTimerRef.current = setTimeout(() => {
      tapCountRef.current = 0;
    }, 800);
    if (tapCountRef.current >= 10) {
      tapCountRef.current = 0;
      onLogout();
    }
  };

  const formatCaptureTimestamp = (value: Date) =>
    value.toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });

  const handleSelectClass = (className: string) => {
    setSelectedClass(className);
    setSelectedMission("");
    setCapturedImage(null);
    setCapturedAt(null);
    setUploadSuccess(false);
    setCameraError(null);
    setUploadError(null);
    setCurrentStep("mission");
  };

  const handleSelectMission = (missionName: string) => {
    setSelectedMission(missionName);
    setCapturedImage(null);
    setCapturedAt(null);
    setUploadSuccess(false);
    setCameraError(null);
    setUploadError(null);
    setCurrentStep("photo");
  };

  const handleAndroidFileCapture = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === "string") {
        setCapturedImage(reader.result);
        setCapturedAt(new Date());
        setUploadSuccess(false);
        setCameraError(null);
        setUploadError(null);
      }
    };
    reader.readAsDataURL(file);
    event.target.value = "";
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    setCapturedAt(null);
    setUploadSuccess(false);
    setCameraError(null);
    setUploadError(null);
    fileInputRef.current?.click();
  };

  const uploadPhoto = async () => {
    if (!selectedClass || !selectedMission || !capturedImage) {
      return;
    }

    setUploadError(null);
    setIsUploading(true);

    try {
      await apiRequest<{ success: boolean; id: string }>("actions.create", {
        method: "POST",
        token: token ?? undefined,
        body: {
          class_name: selectedClass,
          mission_type: selectedMission,
          image_url: capturedImage,
          timestamp: (capturedAt ?? new Date()).toISOString(),
        },
      });

      setIsUploading(false);
      setUploadSuccess(true);
      setTimeout(() => {
        setCapturedImage(null);
        setCapturedAt(null);
        setSelectedClass("");
        setSelectedMission("");
        setCurrentStep("class");
        setUploadSuccess(false);
      }, 3000);
    } catch (error) {
      setIsUploading(false);
      setUploadError(
        error instanceof Error
          ? error.message
          : "Errore durante l'invio della foto",
      );
    }
  };

  const renderMainFunction = () => {
    if (uploadSuccess) {
      return (
        <Card className="w-full max-w-3xl border-2 border-emerald-300 bg-emerald-600 text-center animate-in zoom-in-95 duration-500">
          <div className="text-6xl mb-4">✅</div>
          <div className="text-3xl font-black text-white mb-2">
            Foto inviata!
          </div>
          <div className="text-emerald-100 font-medium">
            L'azione è in attesa di valutazione
          </div>
          <div className="mt-4 text-emerald-50 font-semibold">
            Classe {selectedClass} · {selectedMission}
            {capturedAt ? ` · ${formatCaptureTimestamp(capturedAt)}` : ""}
          </div>
        </Card>
      );
    }

    if (currentStep === "class") {
      return (
        <Card className="w-full max-w-4xl border-2 border-emerald-200">
          <h2 className="text-3xl font-black text-slate-800 mb-2">
            Seleziona la classe
          </h2>
          <p className="text-stone-500 font-medium mb-6">
            Scegli la classe per cui stai caricando la missione.
          </p>
          {setupError && (
            <div className="mb-4 text-sm font-bold text-red-600">
              {setupError}
            </div>
          )}
          {isLoadingSetup && (
            <div className="mb-4 text-sm font-bold text-stone-500">
              Caricamento classi...
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {!isLoadingSetup && classes.length === 0 && (
              <div className="col-span-full text-stone-500 font-medium">
                Nessuna classe attiva disponibile nel database.
              </div>
            )}
            {classes.map((cls) => (
              <button
                key={cls}
                onClick={() => handleSelectClass(cls)}
                className="p-5 rounded-2xl font-black text-2xl transition bg-stone-100 text-slate-700 hover:bg-emerald-100 hover:text-emerald-700 border-2 border-stone-200"
              >
                {cls}
              </button>
            ))}
          </div>
        </Card>
      );
    }

    if (currentStep === "mission") {
      return (
        <Card className="w-full max-w-4xl border-2 border-emerald-200">
          <h2 className="text-3xl font-black text-slate-800 mb-2">
            Seleziona la missione
          </h2>
          <p className="text-stone-500 font-medium mb-6">
            Classe selezionata:{" "}
            <span className="font-black text-slate-700">{selectedClass}</span>
          </p>
          {setupError && (
            <div className="mb-4 text-sm font-bold text-red-600">
              {setupError}
            </div>
          )}
          {isLoadingSetup && (
            <div className="mb-4 text-sm font-bold text-stone-500">
              Caricamento missioni...
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {!isLoadingSetup && missionOptions.length === 0 && (
              <div className="text-stone-500 font-medium">
                Nessuna missione attiva disponibile nel database.
              </div>
            )}
            {missionOptions.map((missionName) => (
              <button
                key={missionName}
                onClick={() => handleSelectMission(missionName)}
                className="text-left bg-stone-50 border-2 border-stone-200 rounded-2xl px-5 py-4 font-black text-slate-700 hover:border-emerald-300 hover:bg-emerald-50 transition"
              >
                {missionName}
              </button>
            ))}
          </div>
          <div className="mt-6">
            <button
              onClick={() => setCurrentStep("class")}
              className="text-stone-500 font-black hover:text-emerald-600 transition uppercase text-xs tracking-[0.2em]"
            >
              ← Torna alla classe
            </button>
          </div>
        </Card>
      );
    }

    if (!capturedImage) {
      return (
        <Card className="w-full max-w-4xl border-2 border-emerald-200">
          <div className="flex gap-4 flex-wrap mb-5">
            <button
              onClick={() => setCurrentStep("class")}
              className="bg-stone-100 text-stone-600 px-4 py-2 rounded-xl font-black hover:bg-stone-200 transition"
            >
              ← Classe
            </button>
            <button
              onClick={() => setCurrentStep("mission")}
              className="bg-stone-100 text-stone-600 px-4 py-2 rounded-xl font-black hover:bg-stone-200 transition"
            >
              ← Missione
            </button>
          </div>
          <div className="aspect-video rounded-2xl bg-gradient-to-br from-emerald-50 to-stone-100 border-2 border-emerald-100 flex flex-col items-center justify-center text-center p-6">
            <div className="text-7xl mb-4">📸</div>
            <div className="text-2xl font-black text-slate-800 mb-2">
              Scatta la foto
            </div>
            <div className="text-stone-500 font-medium mb-6 max-w-lg">
              Classe{" "}
              <span className="font-black text-slate-700">{selectedClass}</span>{" "}
              · Missione{" "}
              <span className="font-black text-slate-700">
                {selectedMission}
              </span>
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="bg-emerald-600 text-white px-10 py-4 rounded-2xl font-black text-lg hover:bg-emerald-700 transition shadow-lg"
            >
              📱 Apri fotocamera
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleAndroidFileCapture}
              className="hidden"
            />
            {cameraError && (
              <div className="mt-4 max-w-lg text-center text-sm font-bold text-amber-800 bg-amber-100 border border-amber-200 rounded-xl px-4 py-3">
                {cameraError}
              </div>
            )}
          </div>
        </Card>
      );
    }

    return (
      <Card className="w-full max-w-4xl border-2 overflow-hidden p-5 lg:p-6">
        <div className="space-y-4">
          <div className="flex gap-4 flex-wrap">
            <button
              onClick={() => setCurrentStep("class")}
              disabled={isUploading}
              className="bg-stone-100 text-stone-600 px-4 py-2 rounded-xl font-black hover:bg-stone-200 transition disabled:opacity-50"
            >
              ← Classe
            </button>
            <button
              onClick={() => setCurrentStep("mission")}
              disabled={isUploading}
              className="bg-stone-100 text-stone-600 px-4 py-2 rounded-xl font-black hover:bg-stone-200 transition disabled:opacity-50"
            >
              ← Missione
            </button>
          </div>
          <div className="aspect-[2/1] rounded-2xl overflow-hidden">
            <img
              src={capturedImage}
              alt="Foto catturata"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="rounded-2xl border-2 border-stone-100 bg-stone-50 p-3 grid grid-cols-1 md:grid-cols-3 gap-2">
            <div>
              <div className="text-xs font-black uppercase tracking-widest text-stone-400">
                Classe
              </div>
              <div className="text-base font-black text-slate-700">
                {selectedClass}
              </div>
            </div>
            <div>
              <div className="text-xs font-black uppercase tracking-widest text-stone-400">
                Missione
              </div>
              <div className="text-base font-black text-slate-700">
                {selectedMission}
              </div>
            </div>
            <div>
              <div className="text-xs font-black uppercase tracking-widest text-stone-400">
                Timestamp
              </div>
              <div className="text-base font-black text-slate-700">
                {capturedAt
                  ? formatCaptureTimestamp(capturedAt)
                  : "Non disponibile"}
              </div>
            </div>
          </div>
          <div className="flex gap-4">
            <button
              onClick={retakePhoto}
              disabled={isUploading}
              className="flex-1 bg-stone-200 text-stone-700 py-4 rounded-2xl font-black text-base hover:bg-stone-300 transition disabled:opacity-50"
            >
              🔄 Rifai foto
            </button>
            <button
              onClick={uploadPhoto}
              disabled={isUploading}
              className="flex-1 bg-emerald-600 text-white py-4 rounded-2xl font-black text-base hover:bg-emerald-700 transition shadow-lg disabled:opacity-50"
            >
              {isUploading ? "⏳ Invio..." : "✓ Invia foto"}
            </button>
          </div>
          {uploadError && (
            <div className="text-sm font-bold text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              {uploadError}
            </div>
          )}
        </div>
      </Card>
    );
  };

  return (
    <div className="h-[100dvh] overflow-hidden bg-stone-50 flex flex-col">
      <div className="bg-white border-b border-stone-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-6 py-5 flex justify-between items-center">
          <div className="text-3xl font-black text-emerald-600 tracking-tighter flex items-center gap-2">
            <span className="text-4xl">🌿</span> GreenHero
            <span
              className="text-stone-400 text-lg font-bold select-none"
              onClick={handleHiddenTap}
            >
              Tablet
            </span>
          </div>
        </div>
      </div>

      <div className="flex-1 p-4 lg:p-6 overflow-hidden">
        <div className="max-w-5xl mx-auto h-full flex items-center justify-center">
          {renderMainFunction()}
        </div>
      </div>
    </div>
  );
};

// --- VISTA: DASHBOARD ---
const Dashboard: React.FC<{
  onLogout: () => void;
  onGoToLanding: () => void;
  user: User;
  token: string | null;
  initialTab?: DashboardTab;
}> = ({ onLogout, onGoToLanding, user, token, initialTab }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const menuItems = getMenuItemsForRole(
    mapRoleToKnownValue(String(user.role ?? "")),
  );
  const resolvedInitialTab =
    initialTab && menuItems.some((item) => item.id === initialTab)
      ? initialTab
      : (menuItems[0]?.id as DashboardTab) || "competizioni";
  const [activeTab, setActiveTab] = useState<DashboardTab>(resolvedInitialTab);
  const [classeTab, setClasseTab] = useState<
    "competizione" | "punteggi" | "classifica"
  >("competizione");
  const [allCompetitions, setAllCompetitions] = useState<Competition[]>([]);
  const [isLoadingCompetitions, setIsLoadingCompetitions] = useState(false);
  const [competitionsError, setCompetitionsError] = useState<string | null>(
    null,
  );
  const [selectedCompetitionId, setSelectedCompetitionId] = useState("");
  const [selectedSchoolFilter, setSelectedSchoolFilter] = useState("all");
  const [selectedSchoolYearFilter, setSelectedSchoolYearFilter] =
    useState("all");
  const [selectedClassFilter, setSelectedClassFilter] = useState("all");
  const [openFilterMenu, setOpenFilterMenu] = useState<
    "school" | "year" | "class" | null
  >(null);
  const filtersContainerRef = React.useRef<HTMLDivElement>(null);
  const [isCreatingCompetition, setIsCreatingCompetition] = useState(false);
  const [creationError, setCreationError] = useState<string | null>(null);
  const [creationSuccess, setCreationSuccess] = useState<string | null>(null);
  const [availableClasses, setAvailableClasses] = useState<
    ClassesListResponse["classes"]
  >([]);
  const [newClassNumber, setNewClassNumber] = useState("");
  const [newClassSection, setNewClassSection] = useState("");
  const [newClassSchoolYear, setNewClassSchoolYear] = useState("");
  const [newClassLocation, setNewClassLocation] = useState("");
  const [manualClassEmailsInput, setManualClassEmailsInput] = useState("");
  const [classJsonEmails, setClassJsonEmails] = useState<string[]>([]);
  const [classJsonFileName, setClassJsonFileName] = useState("");
  const [isCreatingClass, setIsCreatingClass] = useState(false);
  const [classCreationError, setClassCreationError] = useState<string | null>(
    null,
  );
  const [classCreationSuccess, setClassCreationSuccess] = useState<
    string | null
  >(null);
  const [editingClassId, setEditingClassId] = useState<string | null>(null);
  const [editingClassNumber, setEditingClassNumber] = useState("");
  const [editingClassSection, setEditingClassSection] = useState("");
  const [editingClassSchoolYear, setEditingClassSchoolYear] = useState("");
  const [editingClassLocation, setEditingClassLocation] = useState("");
  const [editingManualClassEmailsInput, setEditingManualClassEmailsInput] =
    useState("");
  const [editingClassJsonEmails, setEditingClassJsonEmails] = useState<
    string[]
  >([]);
  const [editingClassJsonFileName, setEditingClassJsonFileName] = useState("");
  const [loadingEditClassId, setLoadingEditClassId] = useState<string | null>(
    null,
  );
  const [savingClassIds, setSavingClassIds] = useState<string[]>([]);
  const [deletingClassIds, setDeletingClassIds] = useState<string[]>([]);
  const [confirmDeleteClassId, setConfirmDeleteClassId] = useState<
    string | null
  >(null);
  const [usersRoleFilter, setUsersRoleFilter] = useState("staff");
  const [managedUsers, setManagedUsers] = useState<UsersListResponse["users"]>(
    [],
  );
  const [isLoadingManagedUsers, setIsLoadingManagedUsers] = useState(false);
  const [managedUsersError, setManagedUsersError] = useState<string | null>(
    null,
  );
  const [managedUsersSuccess, setManagedUsersSuccess] = useState<string | null>(
    null,
  );
  const [updatingUserIds, setUpdatingUserIds] = useState<string[]>([]);
  const [userRoleDrafts, setUserRoleDrafts] = useState<Record<string, string>>(
    {},
  );
  const [roleTargetEmail, setRoleTargetEmail] = useState("");
  const [roleTargetValue, setRoleTargetValue] = useState("esaminatore");
  const [isUpdatingRoleByEmail, setIsUpdatingRoleByEmail] = useState(false);
  const [classSummary, setClassSummary] = useState<
    ClassSummaryResponse["class"] | null
  >(null);
  const [classStudents, setClassStudents] = useState<
    ClassStudentsResponse["students"]
  >([]);
  const [classRanking, setClassRanking] = useState<
    ClassRankingResponse["ranking"]
  >([]);
  const [isLoadingClassData, setIsLoadingClassData] = useState(false);
  const [classDataError, setClassDataError] = useState<string | null>(null);
  const [classDataRefreshKey, setClassDataRefreshKey] = useState(0);
  const [historyActions, setHistoryActions] = useState<
    Array<ActionsHistoryResponse["actions"][number] & { timestampDate: Date }>
  >([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [showNewCompetitionForm, setShowNewCompetitionForm] = useState(false);
  const [newCompetitionName, setNewCompetitionName] = useState("");
  const [newCompetitionDescription, setNewCompetitionDescription] =
    useState("");
  const [newCompetitionStartDate, setNewCompetitionStartDate] = useState("");
  const [newCompetitionEndDate, setNewCompetitionEndDate] = useState("");
  const [newCompetitionStatus, setNewCompetitionStatus] = useState<
    "active" | "upcoming" | "archived"
  >("active");
  const [selectedClassIds, setSelectedClassIds] = useState<string[]>([]);
  const [newCompetitionMissionIds, setNewCompetitionMissionIds] = useState<
    string[]
  >([]);
  const [missionDefinitions, setMissionDefinitions] = useState<
    MissionDefinition[]
  >([]);
  const [newMissionName, setNewMissionName] = useState("");
  const [newMissionDescription, setNewMissionDescription] = useState("");
  const [newMissionImageFile, setNewMissionImageFile] = useState<File | null>(
    null,
  );
  const [newMissionImagePreview, setNewMissionImagePreview] = useState("");
  const [newMissionPoints, setNewMissionPoints] = useState(10);
  const [missionCreationError, setMissionCreationError] = useState<
    string | null
  >(null);
  const [missionCreationSuccess, setMissionCreationSuccess] = useState<
    string | null
  >(null);
  const [deletingMissionIds, setDeletingMissionIds] = useState<string[]>([]);
  const [confirmDeleteMissionId, setConfirmDeleteMissionId] = useState<
    string | null
  >(null);
  const [savingMissionIds, setSavingMissionIds] = useState<string[]>([]);
  const [editingMissionId, setEditingMissionId] = useState<string | null>(null);
  const [editingMissionName, setEditingMissionName] = useState("");
  const [editingMissionDescription, setEditingMissionDescription] =
    useState("");
  const [editingMissionPoints, setEditingMissionPoints] = useState(10);
  const [editingMissionImageFile, setEditingMissionImageFile] =
    useState<File | null>(null);
  const [editingMissionImagePreview, setEditingMissionImagePreview] =
    useState("");
  const [editingCompetitionId, setEditingCompetitionId] = useState<
    string | null
  >(null);
  const [editingCompetitionName, setEditingCompetitionName] = useState("");
  const [editingCompetitionDescription, setEditingCompetitionDescription] =
    useState("");
  const [editingCompetitionStartDate, setEditingCompetitionStartDate] =
    useState("");
  const [editingCompetitionEndDate, setEditingCompetitionEndDate] =
    useState("");
  const [editingCompetitionStatus, setEditingCompetitionStatus] = useState<
    "active" | "upcoming" | "archived"
  >("active");
  const [editingCompetitionMissionIds, setEditingCompetitionMissionIds] =
    useState<string[]>([]);
  const [editingCompetitionClassIds, setEditingCompetitionClassIds] = useState<
    string[]
  >([]);
  const [savingCompetitionIds, setSavingCompetitionIds] = useState<string[]>(
    [],
  );
  const [deletingCompetitionIds, setDeletingCompetitionIds] = useState<
    string[]
  >([]);
  const [confirmDeleteCompetitionId, setConfirmDeleteCompetitionId] = useState<
    string | null
  >(null);
  const [forceDeleteCompetitionId, setForceDeleteCompetitionId] = useState<
    string | null
  >(null);
  const [rankingModalCompetitionId, setRankingModalCompetitionId] = useState<
    string | null
  >(null);
  const [rankingModalName, setRankingModalName] = useState<string>("");
  const [rankingModalData, setRankingModalData] = useState<
    Array<{
      position: number;
      classId: string;
      className: string;
      score: number;
    }>
  >([]);
  const [isLoadingRankingModal, setIsLoadingRankingModal] = useState(false);

  const openRankingModal = async (
    competitionId: string,
    competitionName: string,
  ) => {
    setRankingModalCompetitionId(competitionId);
    setRankingModalName(competitionName);
    setRankingModalData([]);
    setIsLoadingRankingModal(true);
    try {
      const data = await apiRequest<{
        ranking: Array<{
          position: number;
          classId: string;
          className: string;
          score: number;
        }>;
      }>("class.ranking", {
        token: token ?? undefined,
        query: { competition_id: competitionId },
      });
      setRankingModalData(data.ranking);
    } catch {
      setRankingModalData([]);
    } finally {
      setIsLoadingRankingModal(false);
    }
  };

  const effectiveRole = useMemo(
    () => getEffectiveUserRole(user, allCompetitions),
    [user, allCompetitions],
  );
  const hasActiveCompetition = effectiveRole === "partecipante";

  const loadCompetitions = async () => {
    setIsLoadingCompetitions(true);
    setCompetitionsError(null);

    try {
      const data = await apiRequest<CompetitionsListResponse>(
        "competitions.list",
        {
          token: token ?? undefined,
        },
      );

      const competitions: Competition[] = data.competitions.map(
        (competition) => ({
          id: String(competition.id),
          name: competition.name,
          description: competition.description ?? "",
          startDate: new Date(competition.startDate),
          endDate: new Date(competition.endDate),
          classes: competition.classes ?? [],
          classIds: competition.classIds ?? [],
          missionTypes: competition.missionTypes ?? [],
          status: competition.status,
          school: competition.school ?? "",
          schoolYear: competition.schoolYear ?? "",
          ranking: [],
        }),
      );

      setAllCompetitions(competitions);
    } catch (error) {
      setCompetitionsError(
        error instanceof Error
          ? error.message
          : "Errore nel caricamento competizioni",
      );
    } finally {
      setIsLoadingCompetitions(false);
    }
  };

  useEffect(() => {
    loadCompetitions();
  }, []);

  useEffect(() => {
    if (activeTab === "competizioni" || activeTab === "gestione-competizioni") {
      loadCompetitions();
    }
  }, [activeTab, token]);

  const loadMissionDefinitions = async () => {
    if (effectiveRole !== "amministratore" || !token) return;
    try {
      const data = await apiRequest<{
        missionTypes: Array<{
          id: string;
          name: string;
          description?: string;
          points?: number;
          image_url?: string;
        }>;
      }>("missionTypes.list", { token });
      const mapped: MissionDefinition[] = data.missionTypes.map((mission) => ({
        id: String(mission.id),
        name: mission.name,
        description: sanitizeMissionDescription(mission.description ?? ""),
        imageUrl: mission.image_url ?? "",
        points: Number.isFinite(Number(mission.points))
          ? Math.floor(Number(mission.points))
          : 10,
      }));
      setMissionDefinitions(mapped);
    } catch {
      setMissionDefinitions([]);
    }
  };

  useEffect(() => {
    loadMissionDefinitions();
  }, [effectiveRole, token]);

  const visibleCompetitions = useMemo(
    () =>
      allCompetitions.filter(
        (competition) => competition.status !== "archived",
      ),
    [allCompetitions],
  );

  const schoolFilterOptions = useMemo(
    () =>
      Array.from(
        new Set(visibleCompetitions.map((competition) => competition.school)),
      ).sort(),
    [visibleCompetitions],
  );

  const schoolYearFilterOptions = useMemo(
    () =>
      Array.from(
        new Set(
          visibleCompetitions.map((competition) => competition.schoolYear),
        ),
      ).sort(),
    [visibleCompetitions],
  );

  const classFilterOptions = useMemo(
    () =>
      Array.from(
        new Set(
          visibleCompetitions.flatMap((competition) => competition.classes),
        ),
      ).sort(),
    [visibleCompetitions],
  );

  const filteredCompetitions = useMemo(
    () =>
      visibleCompetitions.filter((competition) => {
        const matchesSchool =
          selectedSchoolFilter === "all" ||
          competition.school === selectedSchoolFilter;
        const matchesSchoolYear =
          selectedSchoolYearFilter === "all" ||
          competition.schoolYear === selectedSchoolYearFilter;
        const matchesClass =
          selectedClassFilter === "all" ||
          competition.classes.includes(selectedClassFilter);

        return matchesSchool && matchesSchoolYear && matchesClass;
      }),
    [
      visibleCompetitions,
      selectedSchoolFilter,
      selectedSchoolYearFilter,
      selectedClassFilter,
    ],
  );

  const selectedCompetition = useMemo(
    () =>
      filteredCompetitions.find(
        (competition) => competition.id === selectedCompetitionId,
      ) ?? null,
    [filteredCompetitions, selectedCompetitionId],
  );
  const selectedSchoolFilterLabel =
    selectedSchoolFilter === "all" ? "Tutte le scuole" : selectedSchoolFilter;
  const selectedSchoolYearFilterLabel =
    selectedSchoolYearFilter === "all"
      ? "Tutti gli anni"
      : selectedSchoolYearFilter;
  const selectedClassFilterLabel =
    selectedClassFilter === "all" ? "Tutte le classi" : selectedClassFilter;

  useEffect(() => {
    if (!selectedCompetitionId) {
      return;
    }

    if (!filteredCompetitions.some((c) => c.id === selectedCompetitionId)) {
      setSelectedCompetitionId("");
    }
  }, [filteredCompetitions, selectedCompetitionId]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        filtersContainerRef.current &&
        !filtersContainerRef.current.contains(event.target as Node)
      ) {
        setOpenFilterMenu(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (activeTab !== "competizioni") {
      return;
    }

    if (selectedCompetitionId || filteredCompetitions.length === 0) {
      return;
    }

    const defaultCompetition = filteredCompetitions[0];

    setSelectedCompetitionId(defaultCompetition.id);
  }, [activeTab, filteredCompetitions, selectedCompetitionId]);

  const loadClasses = async () => {
    if (effectiveRole !== "amministratore") return;
    try {
      const data = await apiRequest<ClassesListResponse>("classes.list", {
        token: token ?? undefined,
      });
      setAvailableClasses(
        data.classes.map((classItem) => ({
          ...classItem,
          id: String(classItem.id),
        })),
      );
    } catch {
      setAvailableClasses([]);
    }
  };

  useEffect(() => {
    loadClasses();
  }, [effectiveRole, token]);

  const handleToggleClassInNewCompetition = (classId: string) => {
    setSelectedClassIds((current) =>
      current.includes(classId)
        ? current.filter((id) => id !== classId)
        : [...current, classId],
    );
  };

  const handleClassJsonUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setClassCreationError(null);
    setClassCreationSuccess(null);
    setClassJsonFileName(file.name);

    try {
      const content = await file.text();
      const parsed = JSON.parse(content) as unknown;
      const emails = parseEmailsFromJsonPayload(parsed);

      if (emails.length === 0) {
        setClassJsonEmails([]);
        setClassCreationError(
          'Il file JSON non contiene email valide. Formato supportato: ["a@b.it"] o { students: [{ email: "a@b.it" }] }',
        );
        return;
      }

      setClassJsonEmails(emails);
    } catch {
      setClassJsonEmails([]);
      setClassCreationError("File JSON non valido.");
    }
  };

  const handleCreateClass = async () => {
    setClassCreationError(null);
    setClassCreationSuccess(null);

    const classNumber = Number(newClassNumber);
    const section = newClassSection.trim().toUpperCase();
    const schoolYear = newClassSchoolYear.trim();
    const location = newClassLocation.trim();

    if (!Number.isInteger(classNumber) || classNumber <= 0) {
      setClassCreationError("Inserisci un numero classe valido.");
      return;
    }

    if (!section) {
      setClassCreationError("Inserisci la sezione della classe.");
      return;
    }

    if (!location) {
      setClassCreationError("Inserisci la scuola della classe.");
      return;
    }

    const manualEmails = parseEmailsFromText(manualClassEmailsInput);
    const mergedEmails = Array.from(
      new Set([...manualEmails, ...classJsonEmails]),
    );

    if (mergedEmails.length === 0) {
      setClassCreationError(
        "Inserisci almeno una email manuale o carica un file JSON con email.",
      );
      return;
    }

    if (!token) {
      setClassCreationError("Sessione non valida. Effettua di nuovo il login.");
      return;
    }

    setIsCreatingClass(true);

    try {
      const data = await apiRequest<{
        id: string;
        assigned_count: number;
        created_count?: number;
        missing_emails: string[];
      }>("classes.create", {
        method: "POST",
        token,
        body: {
          number: classNumber,
          section,
          school_year: schoolYear,
          location,
          student_emails: mergedEmails,
        },
      });

      const classesData = await apiRequest<ClassesListResponse>(
        "classes.list",
        {
          token,
        },
      );
      setAvailableClasses(
        classesData.classes.map((classItem) => ({
          ...classItem,
          id: String(classItem.id),
        })),
      );

      const missingCount = data.missing_emails.length;
      const createdCount = Number(data.created_count ?? 0);
      const successMessage =
        missingCount > 0
          ? `Classe creata. Utenti assegnati: ${data.assigned_count}. Nuovi account creati: ${createdCount}. Email non trovate: ${missingCount}.`
          : `Classe creata con successo. Utenti assegnati: ${data.assigned_count}. Nuovi account creati: ${createdCount}.`;

      setClassCreationSuccess(successMessage);
      setNewClassNumber("");
      setNewClassSection("");
      setNewClassSchoolYear("");
      setNewClassLocation("");
      setManualClassEmailsInput("");
      setClassJsonEmails([]);
      setClassJsonFileName("");
    } catch (error) {
      setClassCreationError(
        error instanceof Error
          ? error.message
          : "Errore durante la creazione della classe",
      );
    } finally {
      setIsCreatingClass(false);
    }
  };

  const handleStartEditClass = async (
    classItem: ClassesListResponse["classes"][number],
  ) => {
    setClassCreationError(null);
    setClassCreationSuccess(null);
    const classId = String(classItem.id);
    setEditingClassId(classId);
    setEditingClassNumber(
      typeof classItem.number === "number"
        ? String(classItem.number)
        : String(classItem.name).replace(/[^0-9]/g, ""),
    );
    setEditingClassSection(
      (classItem.section ?? String(classItem.name).replace(/[0-9\s]/g, ""))
        .toUpperCase()
        .trim(),
    );
    setEditingClassSchoolYear(classItem.school_year ?? "");
    setEditingClassLocation(classItem.location ?? "");
    setEditingManualClassEmailsInput("");
    setEditingClassJsonEmails([]);
    setEditingClassJsonFileName("");

    if (!token) {
      setClassCreationError("Sessione non valida. Effettua di nuovo il login.");
      return;
    }

    setLoadingEditClassId(classId);
    try {
      const data = await apiRequest<ClassEmailsResponse>("classes.emails", {
        token,
        query: { class_id: classId },
      });
      const normalizedEmails = Array.from(
        new Set(
          (data.emails ?? [])
            .map((email) => String(email).trim().toLowerCase())
            .filter((email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)),
        ),
      );
      setEditingManualClassEmailsInput(normalizedEmails.join("\n"));
    } catch (error) {
      setClassCreationError(
        error instanceof Error
          ? error.message
          : "Errore durante il caricamento email della classe",
      );
    } finally {
      setLoadingEditClassId(null);
    }
  };

  const handleCancelEditClass = () => {
    setEditingClassId(null);
    setEditingClassNumber("");
    setEditingClassSection("");
    setEditingClassSchoolYear("");
    setEditingClassLocation("");
    setEditingManualClassEmailsInput("");
    setEditingClassJsonEmails([]);
    setEditingClassJsonFileName("");
    setLoadingEditClassId(null);
  };

  const handleEditingClassJsonUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setClassCreationError(null);
    setClassCreationSuccess(null);
    setEditingClassJsonFileName(file.name);

    try {
      const content = await file.text();
      const parsed = JSON.parse(content) as unknown;
      const emails = parseEmailsFromJsonPayload(parsed);

      if (emails.length === 0) {
        setEditingClassJsonEmails([]);
        setClassCreationError(
          'Il file JSON non contiene email valide. Formato supportato: ["a@b.it"] o { students: [{ email: "a@b.it" }] }',
        );
        return;
      }

      setEditingClassJsonEmails(emails);
    } catch {
      setEditingClassJsonEmails([]);
      setClassCreationError("File JSON non valido.");
    }
  };

  const handleSaveEditClass = async (classId: string) => {
    setClassCreationError(null);
    setClassCreationSuccess(null);

    const classNumber = Number(editingClassNumber);
    const section = editingClassSection.trim().toUpperCase();

    if (!Number.isInteger(classNumber) || classNumber <= 0) {
      setClassCreationError("Inserisci un numero classe valido.");
      return;
    }

    if (!section) {
      setClassCreationError("Inserisci la sezione della classe.");
      return;
    }

    const manualEmails = parseEmailsFromText(editingManualClassEmailsInput);
    const mergedEmails = Array.from(
      new Set([...manualEmails, ...editingClassJsonEmails]),
    );

    if (mergedEmails.length === 0) {
      setClassCreationError(
        "Inserisci almeno una email manuale o carica un file JSON con email.",
      );
      return;
    }

    if (!token) {
      setClassCreationError("Sessione non valida. Effettua di nuovo il login.");
      return;
    }

    setSavingClassIds((current) => [...current, classId]);

    try {
      const data = await apiRequest<{
        success: boolean;
        assigned_count?: number;
        created_count?: number;
        detached_count?: number;
        missing_emails?: string[];
      }>("classes.update", {
        method: "POST",
        token,
        body: {
          class_id: classId,
          number: classNumber,
          section,
          school_year: editingClassSchoolYear.trim(),
          location: editingClassLocation.trim(),
          student_emails: mergedEmails,
        },
      });

      setAvailableClasses((current) =>
        current.map((item) =>
          String(item.id) === classId
            ? {
                ...item,
                number: classNumber,
                section,
                name: `${classNumber}${section}`,
                school_year: editingClassSchoolYear.trim(),
                location: editingClassLocation.trim(),
                student_count:
                  typeof data.assigned_count === "number"
                    ? data.assigned_count
                    : item.student_count,
              }
            : item,
        ),
      );

      handleCancelEditClass();
      const missingCount = Array.isArray(data.missing_emails)
        ? data.missing_emails.length
        : 0;
      const assignedCount = Number(data.assigned_count ?? mergedEmails.length);
      const createdCount = Number(data.created_count ?? 0);
      const detachedCount = Number(data.detached_count ?? 0);
      const successMessage =
        missingCount > 0
          ? `Classe aggiornata. Utenti assegnati: ${assignedCount}. Nuovi account creati: ${createdCount}. Rimossi dalla classe: ${detachedCount}. Email non trovate: ${missingCount}.`
          : `Classe aggiornata con successo. Utenti assegnati: ${assignedCount}. Nuovi account creati: ${createdCount}. Rimossi dalla classe: ${detachedCount}.`;
      setClassCreationSuccess(successMessage);
    } catch (error) {
      setClassCreationError(
        error instanceof Error
          ? error.message
          : "Errore durante la modifica della classe",
      );
    } finally {
      setSavingClassIds((current) => current.filter((id) => id !== classId));
    }
  };

  const handleDeleteClass = async (classId: string) => {
    if (confirmDeleteClassId !== classId) {
      setConfirmDeleteClassId(classId);
      return;
    }

    if (!token) {
      setClassCreationError("Sessione non valida. Effettua di nuovo il login.");
      return;
    }

    setClassCreationError(null);
    setClassCreationSuccess(null);
    setDeletingClassIds((current) => [...current, classId]);

    try {
      await apiRequest<{ success: boolean }>("classes.delete", {
        method: "POST",
        token,
        body: {
          class_id: classId,
        },
      });

      setAvailableClasses((current) =>
        current.filter((item) => String(item.id) !== classId),
      );

      if (editingClassId === classId) {
        handleCancelEditClass();
      }

      setConfirmDeleteClassId(null);
      setClassCreationSuccess("Classe eliminata con successo.");
    } catch (error) {
      setClassCreationError(
        error instanceof Error
          ? error.message
          : "Errore durante l'eliminazione della classe",
      );
    } finally {
      setDeletingClassIds((current) => current.filter((id) => id !== classId));
    }
  };

  const handleToggleMissionInNewCompetition = (missionId: string) => {
    setNewCompetitionMissionIds((current) =>
      current.includes(missionId)
        ? current.filter((id) => id !== missionId)
        : [...current, missionId],
    );
  };

  const handleToggleMissionInEditingCompetition = (missionId: string) => {
    setEditingCompetitionMissionIds((current) =>
      current.includes(missionId)
        ? current.filter((id) => id !== missionId)
        : [...current, missionId],
    );
  };

  const loadManagedUsers = async () => {
    if (!token) {
      setManagedUsers([]);
      setManagedUsersError("Sessione non valida. Effettua di nuovo il login.");
      return;
    }

    setIsLoadingManagedUsers(true);
    setManagedUsersError(null);

    try {
      const roleQuery = usersRoleFilter === "all" ? undefined : usersRoleFilter;
      const data = await apiRequest<UsersListResponse>("users.list", {
        token,
        query: {
          role: roleQuery,
        },
      });

      const normalizedUsers = data.users.map((managedUser) => ({
        ...managedUser,
        id: String(managedUser.id),
        role: mapRoleToKnownValue(String(managedUser.role ?? "")),
      }));

      setManagedUsers(normalizedUsers);
      setUserRoleDrafts((current) => {
        const next = { ...current };
        normalizedUsers.forEach((managedUser) => {
          next[managedUser.id] =
            next[managedUser.id] ?? mapRoleToKnownValue(managedUser.role);
        });
        return next;
      });
    } catch (error) {
      setManagedUsers([]);
      setManagedUsersError(
        error instanceof Error
          ? error.message
          : "Errore durante il caricamento utenti",
      );
    } finally {
      setIsLoadingManagedUsers(false);
    }
  };

  useEffect(() => {
    if (activeTab !== "gestione-utenti" || effectiveRole !== "amministratore") {
      return;
    }

    loadManagedUsers();
  }, [activeTab, effectiveRole, token, usersRoleFilter]);

  useEffect(() => {
    if (activeTab !== "classe") {
      return;
    }

    if (effectiveRole !== "partecipante" || !token) {
      setClassSummary(null);
      setClassStudents([]);
      setClassRanking([]);
      setClassDataError(null);
      return;
    }

    const activeCompetition = allCompetitions.find(
      (competition) =>
        competition.status === "active" &&
        !!user.class &&
        competition.classes.includes(user.class),
    );

    const loadClassData = async () => {
      setIsLoadingClassData(true);
      setClassDataError(null);

      try {
        const [summaryData, studentsData, rankingData] = await Promise.all([
          apiRequest<ClassSummaryResponse>("class.summary", { token }),
          apiRequest<ClassStudentsResponse>("class.students", { token }),
          apiRequest<ClassRankingResponse>("class.ranking", {
            token,
            query: {
              competition_id: activeCompetition?.id,
            },
          }),
        ]);

        setClassSummary(summaryData.class);
        setClassStudents(studentsData.students);
        setClassRanking(rankingData.ranking);
      } catch (error) {
        setClassSummary(null);
        setClassStudents([]);
        setClassRanking([]);
        setClassDataError(
          error instanceof Error
            ? error.message
            : "Errore durante il caricamento dati classe",
        );
      } finally {
        setIsLoadingClassData(false);
      }
    };

    loadClassData();
  }, [
    activeTab,
    effectiveRole,
    token,
    allCompetitions,
    user.class,
    classDataRefreshKey,
  ]);

  useEffect(() => {
    if (activeTab !== "storico") {
      return;
    }

    if (
      !token ||
      (effectiveRole !== "partecipante" && effectiveRole !== "amministratore")
    ) {
      setHistoryActions([]);
      setHistoryError(null);
      return;
    }

    const loadHistory = async () => {
      setIsLoadingHistory(true);
      setHistoryError(null);

      try {
        const data = await apiRequest<ActionsHistoryResponse>(
          "actions.history",
          {
            token,
          },
        );

        setHistoryActions(
          data.actions.map((action) => ({
            ...action,
            timestampDate: new Date(action.timestamp),
          })),
        );
      } catch (error) {
        setHistoryActions([]);
        setHistoryError(
          error instanceof Error
            ? error.message
            : "Errore durante il caricamento storico",
        );
      } finally {
        setIsLoadingHistory(false);
      }
    };

    loadHistory();
  }, [activeTab, effectiveRole, token]);

  const handleUpdateUserRole = async (
    managedUserId: string,
    selectedRole?: string,
    previousRole?: string,
  ) => {
    const nextRole = selectedRole ?? userRoleDrafts[managedUserId];
    const targetUser = managedUsers.find(
      (managedUser) => managedUser.id === managedUserId,
    );

    if (nextRole === "nessuno") {
      if (!token) {
        setManagedUsersError(
          "Sessione non valida. Effettua di nuovo il login.",
        );
        return;
      }

      setManagedUsersError(null);
      setManagedUsersSuccess(null);
      setUpdatingUserIds((current) => [...current, managedUserId]);

      try {
        const isPendingAssignment = managedUserId.startsWith("pending:");
        if (isPendingAssignment) {
          await apiRequest<{ success: boolean }>("users.removeRole", {
            method: "POST",
            token,
            body: {
              email:
                targetUser?.email ?? managedUserId.replace(/^pending:/, ""),
            },
          });
        } else {
          await apiRequest<{ success: boolean }>("users.removeRole", {
            method: "POST",
            token,
            body: {
              user_id: managedUserId,
            },
          });
        }

        setManagedUsers((current) =>
          current.filter((managedUser) => managedUser.id !== managedUserId),
        );
        setManagedUsersSuccess(
          "Ruolo rimosso: utente riportato ai privilegi base.",
        );
      } catch (error) {
        if (previousRole) {
          setUserRoleDrafts((current) => ({
            ...current,
            [managedUserId]: previousRole,
          }));
        }
        setManagedUsersError(
          error instanceof Error
            ? error.message
            : "Errore durante la rimozione ruolo",
        );
      } finally {
        setUpdatingUserIds((current) =>
          current.filter((id) => id !== managedUserId),
        );
      }
      return;
    }

    if (
      !nextRole ||
      !["esaminatore", "amministratore", "tablet"].includes(nextRole)
    ) {
      setManagedUsersError(
        "Ruolo non valido. Seleziona esaminatore, amministratore o tablet.",
      );
      return;
    }

    if (!token) {
      setManagedUsersError("Sessione non valida. Effettua di nuovo il login.");
      return;
    }

    setManagedUsersError(null);
    setManagedUsersSuccess(null);
    setUpdatingUserIds((current) => [...current, managedUserId]);

    try {
      const isPendingAssignment = managedUserId.startsWith("pending:");
      if (isPendingAssignment) {
        await apiRequest<{ success: boolean }>("users.updateRole", {
          method: "POST",
          token,
          body: {
            email: targetUser?.email ?? managedUserId.replace(/^pending:/, ""),
            role: nextRole,
          },
        });
      } else {
        await apiRequest<{ success: boolean }>("users.updateRole", {
          method: "POST",
          token,
          body: {
            user_id: managedUserId,
            role: nextRole,
          },
        });
      }

      setManagedUsers((current) =>
        current.map((managedUser) =>
          managedUser.id === managedUserId
            ? { ...managedUser, role: nextRole }
            : managedUser,
        ),
      );
      setManagedUsersSuccess(
        managedUserId.startsWith("pending:")
          ? "Ruolo assegnato per email in attesa di accesso Google."
          : "Ruolo aggiornato con successo.",
      );
    } catch (error) {
      if (previousRole) {
        setUserRoleDrafts((current) => ({
          ...current,
          [managedUserId]: previousRole,
        }));
      }
      setManagedUsersError(
        error instanceof Error
          ? error.message
          : "Errore durante l'aggiornamento ruolo",
      );
    } finally {
      setUpdatingUserIds((current) =>
        current.filter((id) => id !== managedUserId),
      );
    }
  };

  const handleUpdateUserRoleByEmail = async () => {
    const email = roleTargetEmail.trim().toLowerCase();

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setManagedUsersError("Inserisci una email valida.");
      return;
    }

    if (
      !["esaminatore", "amministratore", "tablet"].includes(roleTargetValue)
    ) {
      setManagedUsersError("Ruolo non valido.");
      return;
    }

    if (!token) {
      setManagedUsersError("Sessione non valida. Effettua di nuovo il login.");
      return;
    }

    setManagedUsersError(null);
    setManagedUsersSuccess(null);
    setIsUpdatingRoleByEmail(true);

    try {
      await apiRequest<{ success: boolean }>("users.updateRole", {
        method: "POST",
        token,
        body: {
          email,
          role: roleTargetValue,
        },
      });

      setManagedUsersSuccess("Ruolo salvato con successo per email.");
      await loadManagedUsers();
    } catch (error) {
      setManagedUsersError(
        error instanceof Error
          ? error.message
          : "Errore durante l'aggiornamento ruolo per email",
      );
    } finally {
      setIsUpdatingRoleByEmail(false);
    }
  };

  const handleCreateMissionDefinition = async () => {
    setMissionCreationError(null);
    setMissionCreationSuccess(null);

    const missionName = newMissionName.trim();
    const missionDescription = newMissionDescription.trim();

    if (!missionName) {
      setMissionCreationError("Inserisci il nome della missione.");
      return;
    }

    if (!missionDescription) {
      setMissionCreationError("Inserisci una descrizione per la missione.");
      return;
    }

    if (!newMissionImageFile) {
      setMissionCreationError(
        "Inserisci un'immagine di esempio per la missione.",
      );
      return;
    }

    if (!Number.isFinite(newMissionPoints) || newMissionPoints <= 0) {
      setMissionCreationError(
        "Il punteggio deve essere un numero maggiore di 0.",
      );
      return;
    }

    if (!token) {
      setMissionCreationError(
        "Sessione non valida. Effettua di nuovo il login.",
      );
      return;
    }

    try {
      const fileData = await readFileAsDataUrl(newMissionImageFile);
      const uploadData = await apiRequest<{ image_url: string }>(
        "missionTypes.uploadImage",
        {
          method: "POST",
          token,
          body: {
            file_name: newMissionImageFile.name,
            file_data: fileData,
          },
        },
      );

      const data = await apiRequest<{ id: string }>("missionTypes.create", {
        method: "POST",
        token,
        body: {
          name: missionName,
          description: missionDescription,
          points: Math.floor(newMissionPoints),
          image_url: uploadData.image_url,
        },
      });

      const newMission: MissionDefinition = {
        id: String(data.id),
        name: missionName,
        description: missionDescription,
        imageUrl: uploadData.image_url,
        points: Math.floor(newMissionPoints),
      };

      setMissionDefinitions((current) => [...current, newMission]);
      setNewMissionName("");
      setNewMissionDescription("");
      setNewMissionImageFile(null);
      setNewMissionImagePreview("");
      setNewMissionPoints(10);
      setMissionCreationSuccess("Missione creata con successo.");
    } catch (error) {
      setMissionCreationError(
        error instanceof Error
          ? error.message
          : "Errore durante la creazione della missione",
      );
    }
  };

  const handleDeleteMissionDefinition = async (missionId: string) => {
    if (confirmDeleteMissionId !== missionId) {
      setConfirmDeleteMissionId(missionId);
      return;
    }

    if (!token) {
      setMissionCreationError(
        "Sessione non valida. Effettua di nuovo il login.",
      );
      return;
    }

    setMissionCreationError(null);
    setMissionCreationSuccess(null);
    setDeletingMissionIds((current) => [...current, missionId]);

    try {
      await apiRequest<{ success: boolean }>("missionTypes.delete", {
        method: "POST",
        token,
        body: {
          mission_type_id: missionId,
        },
      });

      setMissionDefinitions((current) =>
        current.filter((mission) => mission.id !== missionId),
      );
      setConfirmDeleteMissionId(null);
      setMissionCreationSuccess("Missione eliminata con successo.");
    } catch (error) {
      setMissionCreationError(
        error instanceof Error
          ? error.message
          : "Errore durante l'eliminazione della missione",
      );
    } finally {
      setDeletingMissionIds((current) =>
        current.filter((id) => id !== missionId),
      );
    }
  };

  const handleStartEditMissionDefinition = (mission: MissionDefinition) => {
    setMissionCreationError(null);
    setMissionCreationSuccess(null);
    setEditingMissionId(mission.id);
    setEditingMissionName(mission.name);
    setEditingMissionDescription(mission.description);
    setEditingMissionPoints(mission.points);
    setEditingMissionImageFile(null);
    setEditingMissionImagePreview(mission.imageUrl);
  };

  const handleCancelEditMissionDefinition = () => {
    setEditingMissionId(null);
    setEditingMissionName("");
    setEditingMissionDescription("");
    setEditingMissionPoints(10);
    setEditingMissionImageFile(null);
    setEditingMissionImagePreview("");
  };

  const handleSaveEditMissionDefinition = async (missionId: string) => {
    if (!token) {
      setMissionCreationError(
        "Sessione non valida. Effettua di nuovo il login.",
      );
      return;
    }

    const missionName = editingMissionName.trim();
    const missionDescription = editingMissionDescription.trim();

    if (!missionName) {
      setMissionCreationError("Inserisci il nome della missione.");
      return;
    }

    if (!missionDescription) {
      setMissionCreationError("Inserisci una descrizione per la missione.");
      return;
    }

    if (!editingMissionImagePreview.trim()) {
      setMissionCreationError(
        "Inserisci un'immagine di esempio per la missione.",
      );
      return;
    }

    if (!Number.isFinite(editingMissionPoints) || editingMissionPoints <= 0) {
      setMissionCreationError(
        "Il punteggio deve essere un numero maggiore di 0.",
      );
      return;
    }

    setMissionCreationError(null);
    setMissionCreationSuccess(null);
    setSavingMissionIds((current) => [...current, missionId]);

    try {
      let missionImageUrl = editingMissionImagePreview.trim();

      if (editingMissionImageFile) {
        const fileData = await readFileAsDataUrl(editingMissionImageFile);
        const uploadData = await apiRequest<{ image_url: string }>(
          "missionTypes.uploadImage",
          {
            method: "POST",
            token,
            body: {
              file_name: editingMissionImageFile.name,
              file_data: fileData,
            },
          },
        );
        missionImageUrl = uploadData.image_url;
      }

      await apiRequest<{ success: boolean }>("missionTypes.update", {
        method: "POST",
        token,
        body: {
          mission_type_id: missionId,
          name: missionName,
          description: missionDescription,
          points: Math.floor(editingMissionPoints),
          image_url: missionImageUrl,
        },
      });

      setMissionDefinitions((current) =>
        current.map((mission) =>
          mission.id === missionId
            ? {
                ...mission,
                name: missionName,
                description: missionDescription,
                points: Math.floor(editingMissionPoints),
                imageUrl: missionImageUrl,
              }
            : mission,
        ),
      );
      handleCancelEditMissionDefinition();
      setMissionCreationSuccess("Missione aggiornata con successo.");
    } catch (error) {
      setMissionCreationError(
        error instanceof Error
          ? error.message
          : "Errore durante l'aggiornamento della missione",
      );
    } finally {
      setSavingMissionIds((current) =>
        current.filter((id) => id !== missionId),
      );
    }
  };

  const handleCreateCompetition = async () => {
    setCreationError(null);
    setCreationSuccess(null);

    if (
      !newCompetitionName ||
      !newCompetitionStartDate ||
      !newCompetitionEndDate
    ) {
      setCreationError("Compila nome, data inizio e data fine.");
      return;
    }

    if (new Date(newCompetitionStartDate) > new Date(newCompetitionEndDate)) {
      setCreationError(
        "La data di inizio deve essere precedente alla data di fine.",
      );
      return;
    }

    if (newCompetitionMissionIds.length === 0) {
      setCreationError("Aggiungi almeno un tipo missione per la competizione.");
      return;
    }

    if (!token) {
      setCreationError("Sessione non valida. Effettua di nuovo il login.");
      return;
    }

    setIsCreatingCompetition(true);

    try {
      await apiRequest<{ id: string }>("competitions.create", {
        method: "POST",
        token,
        body: {
          name: newCompetitionName.trim(),
          description: newCompetitionDescription.trim(),
          start_date: newCompetitionStartDate,
          end_date: newCompetitionEndDate,
          status: newCompetitionStatus,
          class_ids: selectedClassIds.map((id) => Number(id)),
          mission_ids: newCompetitionMissionIds.map((id) => Number(id)),
          mission_types: missionDefinitions
            .filter((mission) => newCompetitionMissionIds.includes(mission.id))
            .map((mission) => mission.name),
        },
      });

      setCreationSuccess("Competizione creata con successo.");
      setNewCompetitionName("");
      setNewCompetitionDescription("");
      setNewCompetitionStartDate("");
      setNewCompetitionEndDate("");
      setNewCompetitionStatus("active");
      setSelectedClassIds([]);
      setNewCompetitionMissionIds([]);
      setShowNewCompetitionForm(false);
      await loadCompetitions();
    } catch (error) {
      setCreationError(
        error instanceof Error
          ? error.message
          : "Errore durante la creazione della competizione",
      );
    } finally {
      setIsCreatingCompetition(false);
    }
  };

  const handleToggleClassInEditingCompetition = (classId: string) => {
    setEditingCompetitionClassIds((current) =>
      current.includes(classId)
        ? current.filter((id) => id !== classId)
        : [...current, classId],
    );
  };

  const handleStartEditCompetition = (competition: Competition) => {
    setCreationError(null);
    setCreationSuccess(null);
    setEditingCompetitionId(competition.id);
    setEditingCompetitionName(competition.name);
    setEditingCompetitionDescription(competition.description);
    setEditingCompetitionStartDate(toInputDateString(competition.startDate));
    setEditingCompetitionEndDate(toInputDateString(competition.endDate));
    setEditingCompetitionStatus(competition.status);
    setEditingCompetitionClassIds(competition.classIds ?? []);
    const selectedMissionIds = missionDefinitions
      .filter((mission) =>
        competition.missionTypes.some(
          (missionType) =>
            missionType.trim().toLowerCase() ===
            mission.name.trim().toLowerCase(),
        ),
      )
      .map((mission) => mission.id);
    setEditingCompetitionMissionIds(selectedMissionIds);
  };

  const handleCancelEditCompetition = () => {
    setEditingCompetitionId(null);
    setEditingCompetitionName("");
    setEditingCompetitionDescription("");
    setEditingCompetitionStartDate("");
    setEditingCompetitionEndDate("");
    setEditingCompetitionStatus("active");
    setEditingCompetitionMissionIds([]);
    setEditingCompetitionClassIds([]);
  };

  const handleSaveEditCompetition = async (competitionId: string) => {
    setCreationError(null);
    setCreationSuccess(null);

    if (
      !editingCompetitionName.trim() ||
      !editingCompetitionStartDate ||
      !editingCompetitionEndDate
    ) {
      setCreationError("Compila nome, data inizio e data fine.");
      return;
    }

    if (
      new Date(editingCompetitionStartDate) >
      new Date(editingCompetitionEndDate)
    ) {
      setCreationError(
        "La data di inizio deve essere precedente alla data di fine.",
      );
      return;
    }

    if (editingCompetitionMissionIds.length === 0) {
      setCreationError(
        "Seleziona almeno un tipo missione per la competizione.",
      );
      return;
    }

    if (!token) {
      setCreationError("Sessione non valida. Effettua di nuovo il login.");
      return;
    }

    setSavingCompetitionIds((current) => [...current, competitionId]);

    try {
      await apiRequest<{ success: boolean }>("competitions.update", {
        method: "POST",
        token,
        body: {
          competition_id: competitionId,
          name: editingCompetitionName.trim(),
          description: editingCompetitionDescription.trim(),
          start_date: editingCompetitionStartDate,
          end_date: editingCompetitionEndDate,
          status: editingCompetitionStatus,
          class_ids: editingCompetitionClassIds.map((id) => Number(id)),
          mission_ids: editingCompetitionMissionIds.map((id) => Number(id)),
          mission_types: missionDefinitions
            .filter((mission) =>
              editingCompetitionMissionIds.includes(mission.id),
            )
            .map((mission) => mission.name),
        },
      });

      setAllCompetitions((current) =>
        current.map((competition) =>
          competition.id === competitionId
            ? {
                ...competition,
                name: editingCompetitionName.trim(),
                description: editingCompetitionDescription.trim(),
                startDate: new Date(editingCompetitionStartDate),
                endDate: new Date(editingCompetitionEndDate),
                status: editingCompetitionStatus,
                classIds: editingCompetitionClassIds,
                classes: availableClasses
                  .filter((classItem) =>
                    editingCompetitionClassIds.includes(String(classItem.id)),
                  )
                  .map((classItem) => classItem.name),
                missionTypes: missionDefinitions
                  .filter((mission) =>
                    editingCompetitionMissionIds.includes(mission.id),
                  )
                  .map((mission) => mission.name),
              }
            : competition,
        ),
      );

      handleCancelEditCompetition();
      setCreationSuccess("Competizione aggiornata con successo.");
    } catch (error) {
      setCreationError(
        error instanceof Error
          ? error.message
          : "Errore durante l'aggiornamento della competizione",
      );
    } finally {
      setSavingCompetitionIds((current) =>
        current.filter((id) => id !== competitionId),
      );
    }
  };

  const handleDeleteCompetition = async (competitionId: string) => {
    if (confirmDeleteCompetitionId !== competitionId) {
      setConfirmDeleteCompetitionId(competitionId);
      setForceDeleteCompetitionId(null);
      return;
    }

    setCreationError(null);
    setCreationSuccess(null);

    if (!token) {
      setCreationError("Sessione non valida. Effettua di nuovo il login.");
      return;
    }

    setDeletingCompetitionIds((current) => [...current, competitionId]);
    const shouldForceDelete = forceDeleteCompetitionId === competitionId;

    try {
      await apiRequest<{ success: boolean }>("competitions.delete", {
        method: "POST",
        token,
        body: {
          competition_id: competitionId,
          force_delete_actions: shouldForceDelete,
        },
      });

      setAllCompetitions((current) =>
        current.filter((competition) => competition.id !== competitionId),
      );

      if (editingCompetitionId === competitionId) {
        handleCancelEditCompetition();
      }

      setConfirmDeleteCompetitionId(null);
      setForceDeleteCompetitionId(null);
      setCreationSuccess(
        shouldForceDelete
          ? "Competizione eliminata con successo (azioni collegate rimosse)."
          : "Competizione eliminata con successo.",
      );
    } catch (error) {
      if (
        error instanceof Error &&
        error.message.toLowerCase().includes("azioni registrate")
      ) {
        setForceDeleteCompetitionId(competitionId);
        setCreationError(
          "La competizione ha azioni registrate. Premi ancora 'Conferma' per eliminarla insieme alle azioni collegate.",
        );
        return;
      }

      setCreationError(
        error instanceof Error
          ? error.message
          : "Errore durante l'eliminazione della competizione",
      );
    } finally {
      setDeletingCompetitionIds((current) =>
        current.filter((id) => id !== competitionId),
      );
    }
  };

  // --- SEZIONE 1: LA MIA CLASSE (Layout Bozzetto) ---
  const ClasseSection = ({ full = false }) => {
    const activeCompetition = allCompetitions.find(
      (competition) =>
        competition.status === "active" &&
        !!user.class &&
        competition.classes.includes(user.class),
    );

    const classPosition = classSummary
      ? classRanking.find((row) => row.className === classSummary.name)
      : null;

    return (
      <div
        className={`space-y-8 ${full ? "max-w-2xl mx-auto animate-in fade-in slide-in-from-left-4 duration-500" : ""}`}
      >
        <div className="flex items-center gap-3">
          <h2 className="text-3xl font-black flex items-center gap-3 flex-1">
            La mia classe <span className="text-2xl opacity-50">🏠</span>
          </h2>
          <button
            onClick={() => setClassDataRefreshKey((k) => k + 1)}
            disabled={isLoadingClassData}
            title="Ricarica"
            className="p-2 rounded-xl bg-stone-100 hover:bg-emerald-50 hover:text-emerald-700 disabled:opacity-40 transition text-xl leading-none shrink-0"
          >
            <span
              className={isLoadingClassData ? "inline-block animate-spin" : ""}
            >
              ↻
            </span>
          </button>
        </div>

        <div className="flex gap-3 flex-wrap">
          {[
            { id: "competizione", label: "Competizione" },
            { id: "punteggi", label: "Punteggi" },
            { id: "classifica", label: "Classifica" },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() =>
                setClasseTab(
                  item.id as "competizione" | "punteggi" | "classifica",
                )
              }
              className={`px-4 py-2 rounded-xl font-black text-sm transition ${
                classeTab === item.id
                  ? "bg-emerald-600 text-white shadow-lg shadow-emerald-100"
                  : "bg-stone-100 text-stone-500 hover:bg-stone-200"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        {!hasActiveCompetition && (
          <Card className="border-2 border-amber-200 bg-amber-50">
            <div className="flex items-start gap-4">
              <span className="text-2xl">ℹ️</span>
              <div>
                <div className="font-black text-amber-800">
                  Nessuna competizione attiva
                </div>
                <p className="text-amber-700 font-medium text-sm">
                  Non partecipi a nessuna competizione in corso. Questa sezione
                  e disponibile solo in sola consultazione.
                </p>
              </div>
            </div>
          </Card>
        )}

        {classDataError && (
          <Card className="border-2 border-red-200 bg-red-50">
            <div className="font-bold text-red-700">{classDataError}</div>
          </Card>
        )}

        {isLoadingClassData && (
          <Card className="border-2">
            <div className="font-bold text-stone-500">
              Caricamento dati classe...
            </div>
          </Card>
        )}

        {!isLoadingClassData &&
          hasActiveCompetition &&
          classeTab === "competizione" && (
            <div className="space-y-6">
              <Card className="border-2">
                <div className="space-y-4">
                  <div>
                    <div className="text-2xl font-black text-slate-800">
                      {activeCompetition?.name ?? "Competizione attiva"}
                    </div>
                    <div className="text-stone-400 font-bold text-sm tracking-tight">
                      {activeCompetition
                        ? `${activeCompetition.startDate.toLocaleDateString("it-IT")} - ${activeCompetition.endDate.toLocaleDateString("it-IT")}`
                        : "Periodo non disponibile"}
                    </div>
                  </div>
                  <p className="text-stone-600 font-medium">
                    {activeCompetition?.description ||
                      "Dettagli competizione non disponibili."}
                  </p>
                </div>
              </Card>
              <Card className="border-2">
                <div className="text-xs font-black text-stone-400 uppercase tracking-widest mb-4">
                  Missioni attive
                </div>
                <div className="space-y-3">
                  {(activeCompetition?.missionTypes ?? []).length === 0 ? (
                    <div className="text-stone-500 font-medium">
                      Nessuna missione attiva disponibile nel database.
                    </div>
                  ) : (
                    (activeCompetition?.missionTypes ?? []).map(
                      (missionType, i) => (
                        <div
                          key={i}
                          className="flex items-center justify-between bg-stone-50 p-4 rounded-xl border border-stone-100"
                        >
                          <div>
                            <div className="font-black text-slate-700">
                              {missionType}
                            </div>
                          </div>
                          <span className="text-emerald-600 font-black">
                            Attiva
                          </span>
                        </div>
                      ),
                    )
                  )}
                </div>
              </Card>
            </div>
          )}

        {!isLoadingClassData &&
          hasActiveCompetition &&
          classeTab === "punteggi" && (
            <div className="space-y-6">
              <Card className="border-2">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <div className="text-2xl font-black text-slate-800">
                      {classSummary?.name ?? user.class ?? "Classe"}
                    </div>
                    <div className="text-stone-400 font-bold text-sm tracking-tight">
                      {`a.s. ${classSummary?.schoolYear || "N/D"} - sede: ${classSummary?.location || "N/D"}`}
                    </div>
                  </div>
                </div>
                <div className="bg-stone-50 border-2 border-stone-200 rounded-2xl p-6 text-center mb-6 relative overflow-hidden group">
                  <div className="text-stone-400 font-black uppercase text-[10px] tracking-widest mb-1 relative z-10">
                    Punteggio Totale
                  </div>
                  <div className="text-6xl font-black text-slate-800 relative z-10 group-hover:scale-110 transition-transform">
                    {classSummary?.totalPoints ?? 0}
                  </div>
                  <div className="absolute -bottom-4 -right-4 text-6xl opacity-5 transform rotate-12">
                    🏆
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center text-slate-600 bg-emerald-50/50 p-4 rounded-xl border border-emerald-100/50">
                    <span className="font-bold">Missioni completate:</span>
                    <span className="text-emerald-600 font-black text-xl">
                      {classSummary?.completedCount ?? 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-slate-600 bg-amber-50/50 p-4 rounded-xl border border-amber-100/50">
                    <span className="font-bold">In approvazione:</span>
                    <span className="text-amber-500 font-black text-xl">
                      {classSummary?.pendingCount ?? 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-slate-600 bg-red-50/50 p-4 rounded-xl border border-red-100/50">
                    <span className="font-bold">Rifiutate:</span>
                    <span className="text-red-500 font-black text-xl">
                      {classSummary?.rejectedCount ?? 0}
                    </span>
                  </div>
                </div>
              </Card>
              <div className="space-y-4">
                <h3 className="text-xl font-black uppercase text-xs tracking-widest text-stone-400 pl-2">
                  Lista studenti:
                </h3>
                <Card className="max-h-80 overflow-y-auto hide-scrollbar space-y-3 p-4 border-2">
                  {classStudents.length === 0 ? (
                    <div className="text-stone-500 font-medium">
                      Nessuno studente presente nel database.
                    </div>
                  ) : (
                    classStudents.map((student, i) => (
                      <div
                        key={student.id || i}
                        className="flex items-center gap-4 p-3 bg-stone-50 rounded-xl border border-stone-100 hover:border-emerald-200 transition group"
                      >
                        <div className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center font-bold text-lg shadow-sm group-hover:bg-emerald-600 group-hover:text-white transition-colors duration-300">
                          🌿
                        </div>
                        <span className="font-bold text-slate-700">
                          {student.name}
                        </span>
                      </div>
                    ))
                  )}
                </Card>
              </div>
            </div>
          )}

        {!isLoadingClassData &&
          hasActiveCompetition &&
          classeTab === "classifica" && (
            <Card className="border-2">
              <div className="space-y-4">
                <div className="text-xs font-black text-stone-400 uppercase tracking-widest">
                  Posizionamento classe
                </div>
                <div className="bg-emerald-50 border-2 border-emerald-100 rounded-2xl p-6 flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    <span className="text-3xl font-black text-emerald-600">
                      #{classPosition?.position ?? "-"}
                    </span>
                    <span className="text-xl font-black text-slate-800">
                      {classSummary?.name ?? user.class ?? "Classe"}
                    </span>
                  </div>
                  <span className="text-3xl font-black text-emerald-600">
                    {classPosition?.score ?? 0}
                  </span>
                </div>
                {classRanking
                  .filter(
                    (row) =>
                      row.className !==
                      (classSummary?.name ?? user.class ?? ""),
                  )
                  .map((item) => (
                    <div
                      key={item.classId}
                      className="bg-white border-2 border-stone-100 rounded-2xl p-4 flex justify-between items-center"
                    >
                      <div className="flex items-center gap-4">
                        <span className="text-lg font-black text-stone-300">
                          #{item.position}
                        </span>
                        <span className="font-black text-slate-700">
                          {item.className}
                        </span>
                      </div>
                      <span className="font-black text-emerald-600">
                        {item.score}
                      </span>
                    </div>
                  ))}
              </div>
            </Card>
          )}
      </div>
    );
  };

  // --- SEZIONE MISSIONI DA VALIDARE (per Esaminatori) ---
  const ValidazioniSection = ({ full = false }) => {
    const [actions, setActions] = useState<Action[]>([]);
    const [isLoadingActions, setIsLoadingActions] = useState(false);
    const [actionsError, setActionsError] = useState<string | null>(null);
    const [competitionFilter, setCompetitionFilter] = useState("tutte");
    const [typeFilter, setTypeFilter] = useState("tutte");
    const [refreshKey, setRefreshKey] = useState(0);
    const [turboMode, setTurboMode] = useState(false);
    const [turboInitialCount, setTurboInitialCount] = useState(0);
    useEffect(() => {
      if (
        effectiveRole !== "esaminatore" &&
        effectiveRole !== "amministratore"
      ) {
        return;
      }

      const loadPendingActions = async () => {
        setIsLoadingActions(true);
        setActionsError(null);

        if (!token) {
          setActions([]);
          setActionsError("Sessione non valida. Effettua di nuovo il login.");
          setIsLoadingActions(false);
          return;
        }

        try {
          const data = await apiRequest<PendingActionsResponse>(
            "actions.pending",
            {
              token,
            },
          );

          setActions(
            data.actions.map((action) => ({
              ...action,
              imageUrl: resolveApiImageUrl(
                String(action.imageUrl ?? action.image_url ?? ""),
              ),
              timestamp: new Date(action.timestamp),
            })),
          );
        } catch (error) {
          setActions([]);
          setActionsError(
            error instanceof Error
              ? error.message
              : "Errore caricamento missioni da validare",
          );
        } finally {
          setIsLoadingActions(false);
        }
      };

      loadPendingActions();
    }, [token, effectiveRole, refreshKey]);

    const handleEvaluate = async (
      id: string,
      status: "approved" | "partial" | "rejected",
    ) => {
      if (!token) {
        alert("Sessione non valida. Effettua di nuovo il login.");
        return;
      }

      try {
        await apiRequest<{ success: boolean }>("actions.evaluate", {
          method: "POST",
          token,
          body: {
            action_id: id,
            status,
          },
        });

        setActions((current) => current.filter((action) => action.id !== id));
      } catch (error) {
        alert(
          error instanceof Error
            ? error.message
            : "Errore durante la valutazione",
        );
      }
    };

    const competitions = Array.from(
      new Set(actions.map((a) => a.competitionName).filter(Boolean)),
    ) as string[];
    const missionTypes = Array.from(
      new Set(actions.map((a) => a.missionType).filter(Boolean)),
    ) as string[];

    const filteredActions = actions.filter((action) => {
      const competitionOk =
        competitionFilter === "tutte" ||
        action.competitionName === competitionFilter;
      const typeOk =
        typeFilter === "tutte" || action.missionType === typeFilter;
      return competitionOk && typeOk;
    });

    return (
      <div
        className={`space-y-8 ${full ? "max-w-5xl mx-auto animate-in fade-in duration-500" : ""}`}
      >
        <div className="flex items-center gap-3">
          <h2 className="text-3xl font-black flex items-center gap-3 flex-1">
            Missioni da validare <span className="text-2xl opacity-50">✓</span>
            {actions.length > 0 && (
              <span className="text-lg bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full">
                {actions.length}
              </span>
            )}
          </h2>
          {!isLoadingActions && filteredActions.length > 0 && (
            <button
              onClick={() => {
                setTurboInitialCount(filteredActions.length);
                setTurboMode(true);
              }}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-black transition text-sm shrink-0"
            >
              ⚡ Validazione turbo
            </button>
          )}
          <button
            onClick={() => setRefreshKey((k) => k + 1)}
            disabled={isLoadingActions}
            title="Ricarica"
            className="p-2 rounded-xl bg-stone-100 hover:bg-emerald-50 hover:text-emerald-700 disabled:opacity-40 transition text-xl leading-none shrink-0"
          >
            <span
              className={isLoadingActions ? "inline-block animate-spin" : ""}
            >
              ↻
            </span>
          </button>
        </div>
        <p className="text-stone-500 font-medium">
          Valuta le azioni caricate dalle classi e assegna i punteggi
          appropriati.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <StyledFilterDropdown
            value={competitionFilter}
            onChange={setCompetitionFilter}
            options={[
              { value: "tutte", label: "Tutte le competizioni" },
              ...competitions.map((competition) => ({
                value: competition,
                label: competition,
              })),
            ]}
          />
          <StyledFilterDropdown
            value={typeFilter}
            onChange={setTypeFilter}
            options={[
              { value: "tutte", label: "Tutte le tipologie" },
              ...missionTypes.map((missionType) => ({
                value: missionType,
                label: missionType,
              })),
            ]}
          />
        </div>

        {actionsError && (
          <Card className="border-2 border-red-200 bg-red-50">
            <div className="font-bold text-red-700">{actionsError}</div>
          </Card>
        )}

        <div className="space-y-6">
          {isLoadingActions && (
            <Card className="p-8 text-center">
              <div className="font-black text-stone-500">
                Caricamento missioni...
              </div>
            </Card>
          )}

          {!isLoadingActions && filteredActions.length === 0 ? (
            <Card className="p-12 text-center">
              <div className="text-6xl mb-4">✅</div>
              <h3 className="text-xl font-black text-stone-400">
                Nessuna azione da valutare
              </h3>
              <p className="text-stone-400 mt-2">
                Ottimo lavoro! Tutte le azioni sono state valutate.
              </p>
            </Card>
          ) : (
            filteredActions.map((action) => (
              <Card key={action.id} className="border-2 overflow-hidden">
                <div className="flex flex-col md:flex-row gap-6">
                  <img
                    src={action.imageUrl}
                    alt="Azione"
                    className="w-full md:w-64 h-64 object-cover rounded-xl"
                  />
                  <div className="flex-1 space-y-4">
                    <div>
                      <div className="text-2xl font-black text-slate-800">
                        Classe {action.className}
                      </div>
                      <div className="text-stone-400 font-medium text-sm">
                        Caricato il{" "}
                        {action.timestamp.toLocaleDateString("it-IT")}
                      </div>
                    </div>
                    <div className="bg-stone-50 p-4 rounded-xl space-y-2">
                      <div className="text-xs font-black text-stone-400 uppercase tracking-widest">
                        Dettagli
                      </div>
                      <div className="text-sm text-stone-600">
                        <span className="font-black">Mittente:</span>{" "}
                        {action.senderName || "N/D"}
                      </div>
                      <div className="text-sm text-stone-600">
                        <span className="font-black">Competizione:</span>{" "}
                        {action.competitionName || "N/D"}
                      </div>
                      <div className="text-sm text-stone-600">
                        <span className="font-black">Tipo missione:</span>{" "}
                        {action.missionType || "N/D"}
                      </div>
                      <div className="text-sm text-stone-600">
                        <span className="font-black">Descrizione:</span>{" "}
                        {action.description || "N/D"}
                      </div>
                    </div>
                    <div className="bg-stone-50 p-4 rounded-xl">
                      <div className="text-xs font-black text-stone-400 uppercase tracking-widest mb-2">
                        Tipo Azione
                      </div>
                      <div className="font-bold">
                        💡 {action.missionType || "N/D"}
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleEvaluate(action.id, "approved")}
                        className="flex-1 bg-emerald-600 text-white py-3 rounded-xl font-bold hover:bg-emerald-700 transition"
                      >
                        ✓ Completa (+10 pt)
                      </button>
                      <button
                        onClick={() => handleEvaluate(action.id, "partial")}
                        className="flex-1 bg-amber-500 text-white py-3 rounded-xl font-bold hover:bg-amber-600 transition"
                      >
                        ≈ Parziale (+5 pt)
                      </button>
                      <button
                        onClick={() => handleEvaluate(action.id, "rejected")}
                        className="flex-1 bg-red-500 text-white py-3 rounded-xl font-bold hover:bg-red-600 transition"
                      >
                        ✕ Rifiuta (0 pt)
                      </button>
                    </div>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>

        {turboMode && (
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 lg:p-8"
            style={{
              backgroundColor: "rgba(15,23,42,0.55)",
              backdropFilter: "blur(4px)",
            }}
          >
            <div className="bg-white rounded-3xl shadow-2xl border-2 border-stone-100 flex flex-col w-full h-full max-w-6xl max-h-[90dvh] overflow-hidden animate-in zoom-in-95 fade-in duration-200">
              {/* Barra superiore */}
              <div className="flex items-center justify-between px-6 py-4 border-b-2 border-stone-100 shrink-0">
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-black text-emerald-600 tracking-tighter flex items-center gap-2">
                    ⚡ Validazione Turbo
                  </span>
                  {filteredActions.length > 0 && (
                    <span className="bg-amber-100 text-amber-700 border-2 border-amber-200 px-3 py-1 rounded-full text-sm font-black">
                      {turboInitialCount - filteredActions.length + 1} /{" "}
                      {turboInitialCount}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setTurboMode(false)}
                  className="bg-stone-100 hover:bg-red-50 hover:text-red-600 text-stone-600 px-5 py-2 rounded-xl font-black transition text-sm"
                >
                  ✕ Interrompi
                </button>
              </div>

              {filteredActions.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center gap-6 text-center px-6">
                  <div className="text-7xl">🎉</div>
                  <div className="text-slate-800 text-3xl font-black">
                    Tutte le missioni validate!
                  </div>
                  <p className="text-stone-500 font-medium">
                    Hai completato la validazione turbo.
                  </p>
                  <button
                    onClick={() => setTurboMode(false)}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 rounded-2xl font-black transition"
                  >
                    Chiudi
                  </button>
                </div>
              ) : (
                <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
                  {/* Foto ingrandita */}
                  <div className="flex-1 bg-stone-900 rounded-bl-3xl flex items-center justify-center overflow-hidden">
                    <img
                      src={filteredActions[0].imageUrl}
                      alt="Azione"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  {/* Pannello dettagli + pulsanti */}
                  <div className="w-full lg:w-96 bg-white flex flex-col shrink-0 border-t-2 lg:border-t-0 lg:border-l-2 border-stone-100">
                    <div className="flex-1 overflow-y-auto p-6 space-y-4">
                      <div>
                        <div className="text-2xl font-black text-slate-800">
                          Classe {filteredActions[0].className}
                        </div>
                        <div className="text-stone-400 font-medium text-sm mt-1">
                          Caricato il{" "}
                          {filteredActions[0].timestamp.toLocaleDateString(
                            "it-IT",
                          )}
                        </div>
                      </div>
                      <div className="bg-stone-50 border-2 border-stone-100 p-4 rounded-2xl space-y-2">
                        <div className="text-xs font-black text-stone-400 uppercase tracking-widest">
                          Dettagli
                        </div>
                        <div className="text-sm text-stone-600">
                          <span className="font-black">Mittente:</span>{" "}
                          {filteredActions[0].senderName || "N/D"}
                        </div>
                        <div className="text-sm text-stone-600">
                          <span className="font-black">Competizione:</span>{" "}
                          {filteredActions[0].competitionName || "N/D"}
                        </div>
                        <div className="text-sm text-stone-600">
                          <span className="font-black">Tipo missione:</span>{" "}
                          {filteredActions[0].missionType || "N/D"}
                        </div>
                        <div className="text-sm text-stone-600">
                          <span className="font-black">Descrizione:</span>{" "}
                          {filteredActions[0].description || "N/D"}
                        </div>
                      </div>
                    </div>
                    <div className="p-4 border-t-2 border-stone-100 space-y-2 shrink-0">
                      <button
                        onClick={() =>
                          handleEvaluate(filteredActions[0].id, "approved")
                        }
                        className="w-full bg-emerald-600 text-white py-4 rounded-2xl font-black text-lg hover:bg-emerald-700 transition"
                      >
                        ✓ Completa (+10 pt)
                      </button>
                      <button
                        onClick={() =>
                          handleEvaluate(filteredActions[0].id, "partial")
                        }
                        className="w-full bg-amber-500 text-white py-4 rounded-2xl font-black text-lg hover:bg-amber-600 transition"
                      >
                        ≈ Parziale (+5 pt)
                      </button>
                      <button
                        onClick={() =>
                          handleEvaluate(filteredActions[0].id, "rejected")
                        }
                        className="w-full bg-red-500 text-white py-4 rounded-2xl font-black text-lg hover:bg-red-600 transition"
                      >
                        ✕ Rifiuta (0 pt)
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // --- SEZIONE GESTIONE MISSIONI (per Amministratori) ---
  const GestioneMissioniSection = ({ full = false }) => (
    <div
      className={`space-y-8 ${full ? "max-w-6xl mx-auto animate-in fade-in duration-500" : ""}`}
    >
      <div className="flex items-center gap-3">
        <h2 className="text-3xl font-black flex items-center gap-3 flex-1">
          GESTIONE MISSIONI <span className="text-2xl opacity-50">🎯</span>
        </h2>
        <button
          onClick={() => loadMissionDefinitions()}
          title="Ricarica"
          className="p-2 rounded-xl bg-stone-100 hover:bg-emerald-50 hover:text-emerald-700 transition text-xl leading-none shrink-0"
        >
          ↻
        </button>
      </div>

      <Card className="border-2 space-y-6">
        <div>
          <h3 className="text-xl font-black">Nuova missione</h3>
          <p className="text-stone-500 text-sm">
            Crea una missione definendo nome, punteggio, descrizione e immagine.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            value={newMissionName}
            onChange={(e) => setNewMissionName(e.target.value)}
            placeholder="Nome missione"
            className="bg-stone-50 border-2 border-stone-100 rounded-xl px-4 py-3 font-medium"
          />
          <input
            type="number"
            min={1}
            value={newMissionPoints}
            onChange={(e) => setNewMissionPoints(Number(e.target.value))}
            placeholder="Punteggio"
            className="bg-stone-50 border-2 border-stone-100 rounded-xl px-4 py-3 font-medium"
          />
        </div>

        <textarea
          value={newMissionDescription}
          onChange={(e) => setNewMissionDescription(e.target.value)}
          placeholder="Descrizione missione"
          rows={3}
          className="w-full bg-stone-50 border-2 border-stone-100 rounded-xl px-4 py-3 font-medium"
        />

        <div className="space-y-2">
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0] ?? null;
              setNewMissionImageFile(file);

              if (!file) {
                setNewMissionImagePreview("");
                return;
              }

              const reader = new FileReader();
              reader.onload = () => {
                if (typeof reader.result === "string") {
                  setNewMissionImagePreview(reader.result);
                }
              };
              reader.readAsDataURL(file);
            }}
            className="w-full bg-stone-50 border-2 border-stone-100 rounded-xl px-4 py-3 font-medium file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-emerald-100 file:text-emerald-700 file:font-bold hover:file:bg-emerald-200"
          />
          {newMissionImagePreview.trim() && (
            <img
              src={newMissionImagePreview}
              alt="Anteprima missione"
              className="w-full md:w-72 h-40 object-cover rounded-xl border border-stone-200"
            />
          )}
        </div>

        {missionCreationError && (
          <div className="text-sm font-bold text-red-600">
            {missionCreationError}
          </div>
        )}
        {missionCreationSuccess && (
          <div className="text-sm font-bold text-emerald-600">
            {missionCreationSuccess}
          </div>
        )}

        <button
          type="button"
          onClick={handleCreateMissionDefinition}
          className="bg-emerald-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-emerald-700 transition"
        >
          Crea missione
        </button>

        <div className="space-y-3">
          <div className="text-xs font-black text-stone-400 uppercase tracking-widest">
            Missioni create
          </div>
          {missionDefinitions.length === 0 ? (
            <div className="text-stone-500 font-medium">
              Nessuna missione definita.
            </div>
          ) : (
            <div className="space-y-2">
              {missionDefinitions.map((mission) => (
                <div
                  key={mission.id}
                  className="bg-stone-50 border border-stone-100 rounded-xl px-4 py-3"
                >
                  {editingMissionId === mission.id ? (
                    <div className="space-y-3">
                      <input
                        value={editingMissionName}
                        onChange={(e) => setEditingMissionName(e.target.value)}
                        placeholder="Nome missione"
                        className="w-full bg-white border-2 border-stone-200 rounded-xl px-4 py-3 font-medium"
                      />
                      <textarea
                        value={editingMissionDescription}
                        onChange={(e) =>
                          setEditingMissionDescription(e.target.value)
                        }
                        placeholder="Descrizione missione"
                        rows={3}
                        className="w-full bg-white border-2 border-stone-200 rounded-xl px-4 py-3 font-medium"
                      />
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <input
                          type="number"
                          min={1}
                          value={editingMissionPoints}
                          onChange={(e) =>
                            setEditingMissionPoints(Number(e.target.value))
                          }
                          placeholder="Punteggio"
                          className="bg-white border-2 border-stone-200 rounded-xl px-4 py-3 font-medium"
                        />
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0] ?? null;
                            setEditingMissionImageFile(file);

                            if (!file) {
                              const currentMission = missionDefinitions.find(
                                (item) => item.id === mission.id,
                              );
                              setEditingMissionImagePreview(
                                currentMission?.imageUrl ?? "",
                              );
                              return;
                            }

                            const reader = new FileReader();
                            reader.onload = () => {
                              if (typeof reader.result === "string") {
                                setEditingMissionImagePreview(reader.result);
                              }
                            };
                            reader.readAsDataURL(file);
                          }}
                          className="bg-white border-2 border-stone-200 rounded-xl px-4 py-3 font-medium file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-emerald-100 file:text-emerald-700 file:font-bold hover:file:bg-emerald-200"
                        />
                      </div>
                      {editingMissionImagePreview.trim() && (
                        <img
                          src={editingMissionImagePreview}
                          alt={editingMissionName || mission.name}
                          className="w-full md:w-64 h-36 object-cover rounded-xl border border-stone-200"
                        />
                      )}
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            handleSaveEditMissionDefinition(mission.id)
                          }
                          disabled={savingMissionIds.includes(mission.id)}
                          className="px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-lg font-bold text-sm hover:bg-emerald-200 transition disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {savingMissionIds.includes(mission.id)
                            ? "Salvataggio..."
                            : "Salva"}
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelEditMissionDefinition}
                          disabled={savingMissionIds.includes(mission.id)}
                          className="px-3 py-1.5 bg-stone-200 text-stone-700 rounded-lg font-bold text-sm hover:bg-stone-300 transition disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          Annulla
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start justify-between gap-4">
                        <div className="font-black text-slate-700">
                          {mission.name}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              handleStartEditMissionDefinition(mission)
                            }
                            disabled={deletingMissionIds.includes(mission.id)}
                            className="px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg font-bold text-sm hover:bg-amber-200 transition disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            Modifica
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              handleDeleteMissionDefinition(mission.id)
                            }
                            disabled={deletingMissionIds.includes(mission.id)}
                            className={`px-3 py-1.5 rounded-lg font-bold text-sm transition disabled:opacity-60 disabled:cursor-not-allowed ${confirmDeleteMissionId === mission.id ? "bg-red-600 text-white hover:bg-red-700" : "bg-red-100 text-red-700 hover:bg-red-200"}`}
                          >
                            {deletingMissionIds.includes(mission.id)
                              ? "Eliminazione..."
                              : confirmDeleteMissionId === mission.id
                                ? "Conferma"
                                : "Elimina"}
                          </button>
                        </div>
                      </div>
                      <div className="text-sm text-stone-500 mt-1">
                        {mission.description}
                      </div>
                      <div className="text-sm text-stone-500">
                        Punteggio: {mission.points}
                      </div>
                      {mission.imageUrl && (
                        <img
                          src={mission.imageUrl}
                          alt={mission.name}
                          className="mt-2 w-full md:w-64 h-36 object-cover rounded-xl border border-stone-200"
                        />
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );

  // --- SEZIONE GESTIONE COMPETIZIONI (per Amministratori) ---
  const GestioneCompetizioniSection = ({ full = false }) => (
    <div
      className={`space-y-8 ${full ? "max-w-6xl mx-auto animate-in fade-in duration-500" : ""}`}
    >
      <div className="flex items-center gap-3">
        <h2 className="text-3xl font-black flex items-center gap-3 flex-1">
          Gestione competizioni <span className="text-2xl opacity-50">👑</span>
        </h2>
        <button
          onClick={() => loadCompetitions()}
          disabled={isLoadingCompetitions}
          title="Ricarica"
          className="p-2 rounded-xl bg-stone-100 hover:bg-emerald-50 hover:text-emerald-700 disabled:opacity-40 transition text-xl leading-none shrink-0"
        >
          <span
            className={isLoadingCompetitions ? "inline-block animate-spin" : ""}
          >
            ↻
          </span>
        </button>
      </div>

      <Card className="border-2 space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 className="text-xl font-black">Nuova competizione</h3>
            <p className="text-stone-500 text-sm">
              Crea una competizione che sarà visibile nella sezione "Altre
              competizioni".
            </p>
          </div>
          <button
            onClick={() => {
              setShowNewCompetitionForm((current) => !current);
              setCreationError(null);
              setCreationSuccess(null);
            }}
            className="bg-emerald-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-emerald-700 transition"
          >
            {showNewCompetitionForm ? "Chiudi" : "+ Nuova Competizione"}
          </button>
        </div>

        {showNewCompetitionForm && (
          <div className="space-y-4">
            <input
              value={newCompetitionName}
              onChange={(e) => setNewCompetitionName(e.target.value)}
              placeholder="Nome competizione"
              className="w-full bg-stone-50 border-2 border-stone-100 rounded-xl px-4 py-3 font-medium"
            />

            <textarea
              value={newCompetitionDescription}
              onChange={(e) => setNewCompetitionDescription(e.target.value)}
              placeholder="Descrizione competizione"
              rows={3}
              className="w-full bg-stone-50 border-2 border-stone-100 rounded-xl px-4 py-3 font-medium"
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <input
                type="date"
                value={newCompetitionStartDate}
                onChange={(e) => setNewCompetitionStartDate(e.target.value)}
                className="bg-stone-50 border-2 border-stone-100 rounded-xl px-4 py-3 font-medium"
              />
              <input
                type="date"
                value={newCompetitionEndDate}
                onChange={(e) => setNewCompetitionEndDate(e.target.value)}
                className="bg-stone-50 border-2 border-stone-100 rounded-xl px-4 py-3 font-medium"
              />
              <select
                value={newCompetitionStatus}
                onChange={(e) =>
                  setNewCompetitionStatus(
                    e.target.value as "active" | "upcoming" | "archived",
                  )
                }
                className="bg-stone-50 border-2 border-stone-100 rounded-xl px-4 py-3 font-medium"
              >
                <option value="active">Attiva</option>
                <option value="upcoming">In arrivo</option>
                <option value="archived">Archiviata</option>
              </select>
            </div>

            <div className="space-y-2">
              <div className="text-xs font-black text-stone-400 uppercase tracking-widest">
                Classi partecipanti
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {availableClasses.map((classItem) => (
                  <label
                    key={classItem.id}
                    className="flex items-center gap-3 bg-stone-50 border border-stone-100 rounded-xl px-3 py-2"
                  >
                    <input
                      type="checkbox"
                      checked={selectedClassIds.includes(classItem.id)}
                      onChange={() =>
                        handleToggleClassInNewCompetition(String(classItem.id))
                      }
                    />
                    <span className="font-medium text-slate-700">
                      {classItem.name}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-xs font-black text-stone-400 uppercase tracking-widest">
                Tipi missioni eseguibili
              </div>
              {missionDefinitions.length === 0 ? (
                <div className="text-sm text-stone-500">
                  Nessun tipo missione disponibile. Creane prima uno in Gestione
                  missioni.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {missionDefinitions.map((mission) => (
                    <label
                      key={`new-comp-mission-${mission.id}`}
                      className="flex items-center gap-3 bg-stone-50 border border-stone-100 rounded-xl px-3 py-2"
                    >
                      <input
                        type="checkbox"
                        checked={newCompetitionMissionIds.includes(mission.id)}
                        onChange={() =>
                          handleToggleMissionInNewCompetition(mission.id)
                        }
                      />
                      <span className="font-medium text-slate-700">
                        {mission.name}
                      </span>
                    </label>
                  ))}
                </div>
              )}
              {newCompetitionMissionIds.length === 0 ? (
                <div className="text-sm text-stone-500">
                  Nessun tipo missione aggiunto.
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {missionDefinitions
                    .filter((mission) =>
                      newCompetitionMissionIds.includes(mission.id),
                    )
                    .map((mission) => (
                      <span
                        key={mission.id}
                        className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full text-sm font-semibold"
                      >
                        {mission.name}
                      </span>
                    ))}
                </div>
              )}
            </div>

            {creationError && (
              <div className="text-red-600 font-medium">{creationError}</div>
            )}
            {creationSuccess && (
              <div className="text-emerald-600 font-medium">
                {creationSuccess}
              </div>
            )}

            <button
              onClick={handleCreateCompetition}
              disabled={isCreatingCompetition}
              className="bg-emerald-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-emerald-700 transition disabled:opacity-60"
            >
              {isCreatingCompetition ? "Creazione..." : "Crea competizione"}
            </button>
          </div>
        )}
      </Card>

      <Card className="border-2">
        <h3 className="text-xl font-black mb-6">Competizioni create</h3>
        <div className="space-y-3">
          {allCompetitions.map((competition) => (
            <div
              key={competition.id}
              className="bg-stone-50 border border-stone-100 rounded-xl px-4 py-3"
            >
              {editingCompetitionId === competition.id ? (
                <div className="space-y-3">
                  <input
                    value={editingCompetitionName}
                    onChange={(e) => setEditingCompetitionName(e.target.value)}
                    placeholder="Nome competizione"
                    className="w-full bg-white border-2 border-stone-200 rounded-xl px-4 py-3 font-medium"
                  />
                  <textarea
                    value={editingCompetitionDescription}
                    onChange={(e) =>
                      setEditingCompetitionDescription(e.target.value)
                    }
                    placeholder="Descrizione competizione"
                    rows={3}
                    className="w-full bg-white border-2 border-stone-200 rounded-xl px-4 py-3 font-medium"
                  />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input
                      type="date"
                      value={editingCompetitionStartDate}
                      onChange={(e) =>
                        setEditingCompetitionStartDate(e.target.value)
                      }
                      className="bg-white border-2 border-stone-200 rounded-xl px-4 py-3 font-medium"
                    />
                    <input
                      type="date"
                      value={editingCompetitionEndDate}
                      onChange={(e) =>
                        setEditingCompetitionEndDate(e.target.value)
                      }
                      className="bg-white border-2 border-stone-200 rounded-xl px-4 py-3 font-medium"
                    />
                    <select
                      value={editingCompetitionStatus}
                      onChange={(e) =>
                        setEditingCompetitionStatus(
                          e.target.value as "active" | "upcoming" | "archived",
                        )
                      }
                      className="bg-white border-2 border-stone-200 rounded-xl px-4 py-3 font-medium"
                    >
                      <option value="active">Attiva</option>
                      <option value="upcoming">In arrivo</option>
                      <option value="archived">Archiviata</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <div className="text-xs font-black text-stone-400 uppercase tracking-widest">
                      Classi partecipanti
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {availableClasses.map((classItem) => (
                        <label
                          key={`edit-${competition.id}-${classItem.id}`}
                          className="flex items-center gap-3 bg-white border border-stone-200 rounded-xl px-3 py-2"
                        >
                          <input
                            type="checkbox"
                            checked={editingCompetitionClassIds.includes(
                              String(classItem.id),
                            )}
                            onChange={() =>
                              handleToggleClassInEditingCompetition(
                                String(classItem.id),
                              )
                            }
                          />
                          <span className="font-medium text-slate-700">
                            {classItem.name}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-xs font-black text-stone-400 uppercase tracking-widest">
                      Tipi missioni eseguibili
                    </div>
                    {missionDefinitions.length === 0 ? (
                      <div className="text-sm text-stone-500">
                        Nessun tipo missione disponibile. Creane prima uno in
                        Gestione missioni.
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {missionDefinitions.map((mission) => (
                          <label
                            key={`edit-${competition.id}-mission-${mission.id}`}
                            className="flex items-center gap-3 bg-white border border-stone-200 rounded-xl px-3 py-2"
                          >
                            <input
                              type="checkbox"
                              checked={editingCompetitionMissionIds.includes(
                                mission.id,
                              )}
                              onChange={() =>
                                handleToggleMissionInEditingCompetition(
                                  mission.id,
                                )
                              }
                            />
                            <span className="font-medium text-slate-700">
                              {mission.name}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleSaveEditCompetition(competition.id)}
                      disabled={savingCompetitionIds.includes(competition.id)}
                      className="px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-lg font-bold text-sm hover:bg-emerald-200 transition disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {savingCompetitionIds.includes(competition.id)
                        ? "Salvataggio..."
                        : "Salva"}
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelEditCompetition}
                      disabled={savingCompetitionIds.includes(competition.id)}
                      className="px-3 py-1.5 bg-stone-200 text-stone-700 rounded-lg font-bold text-sm hover:bg-stone-300 transition disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      Annulla
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-4">
                    <div className="font-black text-slate-700">
                      {competition.name}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          openRankingModal(competition.id, competition.name)
                        }
                        className="px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-lg font-bold text-sm hover:bg-emerald-200 transition"
                      >
                        🏆 Classifica
                      </button>
                      <button
                        type="button"
                        onClick={() => handleStartEditCompetition(competition)}
                        disabled={deletingCompetitionIds.includes(
                          competition.id,
                        )}
                        className="px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg font-bold text-sm hover:bg-amber-200 transition disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        Modifica
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteCompetition(competition.id)}
                        disabled={deletingCompetitionIds.includes(
                          competition.id,
                        )}
                        className={`px-3 py-1.5 rounded-lg font-bold text-sm transition disabled:opacity-60 disabled:cursor-not-allowed ${confirmDeleteCompetitionId === competition.id ? "bg-red-600 text-white hover:bg-red-700" : "bg-red-100 text-red-700 hover:bg-red-200"}`}
                      >
                        {deletingCompetitionIds.includes(competition.id)
                          ? "Eliminazione..."
                          : confirmDeleteCompetitionId === competition.id
                            ? "Conferma"
                            : "Elimina"}
                      </button>
                    </div>
                  </div>
                  {competition.description && (
                    <div className="text-sm text-stone-500 mt-1">
                      {competition.description}
                    </div>
                  )}
                  <div className="text-sm text-stone-500">
                    {competition.startDate.toLocaleDateString("it-IT")} -{" "}
                    {competition.endDate.toLocaleDateString("it-IT")} •{" "}
                    {competition.classes.length} classi
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );

  // --- SEZIONE GESTIONE CLASSI (per Amministratori) ---
  const GestioneClassiSection = ({ full = false }) => {
    const manualEmailsCount = parseEmailsFromText(
      manualClassEmailsInput,
    ).length;
    const totalEmails = Array.from(
      new Set([
        ...parseEmailsFromText(manualClassEmailsInput),
        ...classJsonEmails,
      ]),
    ).length;

    return (
      <div
        className={`space-y-8 ${full ? "max-w-6xl mx-auto animate-in fade-in duration-500" : ""}`}
      >
        <div className="flex items-center gap-3">
          <h2 className="text-3xl font-black flex items-center gap-3 flex-1">
            Gestione classi <span className="text-2xl opacity-50">🏫</span>
          </h2>
          <button
            onClick={() => loadClasses()}
            title="Ricarica"
            className="p-2 rounded-xl bg-stone-100 hover:bg-emerald-50 hover:text-emerald-700 transition text-xl leading-none shrink-0"
          >
            ↻
          </button>
        </div>

        <Card className="border-2 space-y-6">
          <div>
            <h3 className="text-xl font-black">Nuova classe</h3>
            <p className="text-stone-500 text-sm">
              Crea una classe inserendo email manualmente o caricando un file
              JSON configurato.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <input
              type="number"
              min={1}
              value={newClassNumber}
              onChange={(e) => setNewClassNumber(e.target.value)}
              placeholder="Numero classe (es. 3)"
              className="bg-stone-50 border-2 border-stone-100 rounded-xl px-4 py-3 font-medium"
            />
            <input
              value={newClassSection}
              onChange={(e) => setNewClassSection(e.target.value.toUpperCase())}
              placeholder="Sezione (es. A)"
              className="bg-stone-50 border-2 border-stone-100 rounded-xl px-4 py-3 font-medium"
            />
            <input
              value={newClassSchoolYear}
              onChange={(e) => setNewClassSchoolYear(e.target.value)}
              placeholder="Anno scolastico (es. 2025/2026)"
              className="bg-stone-50 border-2 border-stone-100 rounded-xl px-4 py-3 font-medium"
            />
            <input
              value={newClassLocation}
              onChange={(e) => setNewClassLocation(e.target.value)}
              placeholder="Scuola"
              className="bg-stone-50 border-2 border-stone-100 rounded-xl px-4 py-3 font-medium"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="text-xs font-black text-stone-400 uppercase tracking-widest">
                Email manuali
              </div>
              <textarea
                value={manualClassEmailsInput}
                onChange={(e) => setManualClassEmailsInput(e.target.value)}
                rows={6}
                placeholder="una.email@scuola.it\nseconda.email@scuola.it"
                className="w-full bg-stone-50 border-2 border-stone-100 rounded-xl px-4 py-3 font-medium"
              />
              <div className="text-xs text-stone-500 font-medium">
                Email valide rilevate: {manualEmailsCount}
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-xs font-black text-stone-400 uppercase tracking-widest">
                File JSON
              </div>
              <input
                type="file"
                accept="application/json,.json"
                onChange={handleClassJsonUpload}
                className="w-full bg-stone-50 border-2 border-stone-100 rounded-xl px-4 py-3 font-medium file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-emerald-100 file:text-emerald-700 file:font-bold hover:file:bg-emerald-200"
              />
              <div className="text-xs text-stone-500 font-medium">
                {classJsonFileName
                  ? `File: ${classJsonFileName} • Email valide: ${classJsonEmails.length}`
                  : "Nessun file JSON caricato"}
              </div>
              <div className="text-xs text-stone-500 font-medium">
                Totale email da associare alla classe: {totalEmails}
              </div>
            </div>
          </div>

          {classCreationError && (
            <div className="text-sm font-bold text-red-600">
              {classCreationError}
            </div>
          )}
          {classCreationSuccess && (
            <div className="text-sm font-bold text-emerald-600">
              {classCreationSuccess}
            </div>
          )}

          <button
            type="button"
            onClick={handleCreateClass}
            disabled={isCreatingClass}
            className="bg-emerald-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-emerald-700 transition disabled:opacity-60"
          >
            {isCreatingClass ? "Creazione..." : "Crea classe"}
          </button>
        </Card>

        <Card className="border-2">
          <h3 className="text-xl font-black mb-6">Classi già aggiunte</h3>
          {availableClasses.length === 0 ? (
            <div className="text-stone-500 font-medium">
              Nessuna classe disponibile.
            </div>
          ) : (
            <div className="space-y-3">
              {availableClasses.map((classItem) => (
                <div
                  key={classItem.id}
                  className="bg-stone-50 border border-stone-100 rounded-xl px-4 py-3"
                >
                  {editingClassId === String(classItem.id) ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <input
                          type="number"
                          min={1}
                          value={editingClassNumber}
                          onChange={(e) =>
                            setEditingClassNumber(e.target.value)
                          }
                          placeholder="Numero classe"
                          className="bg-white border-2 border-stone-200 rounded-xl px-4 py-3 font-medium"
                        />
                        <input
                          value={editingClassSection}
                          onChange={(e) =>
                            setEditingClassSection(e.target.value.toUpperCase())
                          }
                          placeholder="Sezione"
                          className="bg-white border-2 border-stone-200 rounded-xl px-4 py-3 font-medium"
                        />
                        <input
                          value={editingClassSchoolYear}
                          onChange={(e) =>
                            setEditingClassSchoolYear(e.target.value)
                          }
                          placeholder="Anno scolastico"
                          className="bg-white border-2 border-stone-200 rounded-xl px-4 py-3 font-medium"
                        />
                        <input
                          value={editingClassLocation}
                          onChange={(e) =>
                            setEditingClassLocation(e.target.value)
                          }
                          placeholder="Scuola"
                          className="bg-white border-2 border-stone-200 rounded-xl px-4 py-3 font-medium"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <div className="text-xs font-black text-stone-400 uppercase tracking-widest">
                            Email manuali
                          </div>
                          <textarea
                            value={editingManualClassEmailsInput}
                            onChange={(e) =>
                              setEditingManualClassEmailsInput(e.target.value)
                            }
                            rows={6}
                            placeholder="una.email@scuola.it\nseconda.email@scuola.it"
                            className="w-full bg-white border-2 border-stone-200 rounded-xl px-4 py-3 font-medium"
                          />
                          <div className="text-xs text-stone-500 font-medium">
                            {loadingEditClassId === String(classItem.id)
                              ? "Caricamento email in corso..."
                              : `Email valide rilevate: ${parseEmailsFromText(editingManualClassEmailsInput).length}`}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <div className="text-xs font-black text-stone-400 uppercase tracking-widest">
                            File JSON
                          </div>
                          <input
                            type="file"
                            accept="application/json,.json"
                            onChange={handleEditingClassJsonUpload}
                            className="w-full bg-white border-2 border-stone-200 rounded-xl px-4 py-3 font-medium file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-emerald-100 file:text-emerald-700 file:font-bold hover:file:bg-emerald-200"
                          />
                          <div className="text-xs text-stone-500 font-medium">
                            {editingClassJsonFileName
                              ? `File: ${editingClassJsonFileName} • Email valide: ${editingClassJsonEmails.length}`
                              : "Nessun file JSON caricato"}
                          </div>
                          <div className="text-xs text-stone-500 font-medium">
                            Totale email da associare alla classe:{" "}
                            {
                              Array.from(
                                new Set([
                                  ...parseEmailsFromText(
                                    editingManualClassEmailsInput,
                                  ),
                                  ...editingClassJsonEmails,
                                ]),
                              ).length
                            }
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            handleSaveEditClass(String(classItem.id))
                          }
                          disabled={
                            savingClassIds.includes(String(classItem.id)) ||
                            loadingEditClassId === String(classItem.id)
                          }
                          className="px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-lg font-bold text-sm hover:bg-emerald-200 transition disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {savingClassIds.includes(String(classItem.id))
                            ? "Salvataggio..."
                            : "Salva"}
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelEditClass}
                          disabled={
                            savingClassIds.includes(String(classItem.id)) ||
                            loadingEditClassId === String(classItem.id)
                          }
                          className="px-3 py-1.5 bg-stone-200 text-stone-700 rounded-lg font-bold text-sm hover:bg-stone-300 transition disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          Annulla
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start justify-between gap-4">
                        <div className="font-black text-slate-700">
                          {classItem.name}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleStartEditClass(classItem)}
                            disabled={deletingClassIds.includes(
                              String(classItem.id),
                            )}
                            className="px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg font-bold text-sm hover:bg-amber-200 transition disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            Modifica
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              handleDeleteClass(String(classItem.id))
                            }
                            disabled={deletingClassIds.includes(
                              String(classItem.id),
                            )}
                            className={`px-3 py-1.5 rounded-lg font-bold text-sm transition disabled:opacity-60 disabled:cursor-not-allowed ${confirmDeleteClassId === String(classItem.id) ? "bg-red-600 text-white hover:bg-red-700" : "bg-red-100 text-red-700 hover:bg-red-200"}`}
                          >
                            {deletingClassIds.includes(String(classItem.id))
                              ? "Eliminazione..."
                              : confirmDeleteClassId === String(classItem.id)
                                ? "Conferma"
                                : "Elimina"}
                          </button>
                        </div>
                      </div>
                      <div className="text-sm text-stone-500 mt-1">
                        Anno scolastico: {classItem.school_year || "N/D"} •
                        Scuola: {classItem.location || "N/D"}
                        {typeof classItem.student_count === "number"
                          ? ` • Studenti: ${classItem.student_count}`
                          : ""}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    );
  };

  // --- SEZIONE STORICO ---
  const StoricoSection = ({ full = false }) => (
    <div
      className={`space-y-8 ${full ? "max-w-4xl mx-auto animate-in fade-in duration-500" : ""}`}
    >
      <h2 className="text-3xl font-black flex items-center gap-3">
        Storico Azioni <span className="text-2xl opacity-50">📜</span>
      </h2>

      {historyError && (
        <Card className="border-2 border-red-200 bg-red-50">
          <div className="font-bold text-red-700">{historyError}</div>
        </Card>
      )}

      {isLoadingHistory && (
        <Card className="border-2">
          <div className="font-bold text-stone-500">Caricamento storico...</div>
        </Card>
      )}

      <div className="space-y-4">
        {!isLoadingHistory && historyActions.length === 0 ? (
          <Card className="border-2">
            <div className="text-stone-500 font-medium">
              Nessuna azione storica presente nel database.
            </div>
          </Card>
        ) : (
          historyActions.map((item, i) => (
            <Card key={i} className="border-2">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${
                      item.status === "approved"
                        ? "bg-emerald-100"
                        : item.status === "partial"
                          ? "bg-amber-100"
                          : "bg-red-100"
                    }`}
                  >
                    {item.status === "approved"
                      ? "✓"
                      : item.status === "partial"
                        ? "≈"
                        : "✕"}
                  </div>
                  <div>
                    <div className="font-black text-slate-700">
                      {item.missionType}
                    </div>
                    <div className="text-sm text-stone-500">
                      {item.timestampDate.toLocaleDateString("it-IT")} •{" "}
                      {item.competitionName || "Competizione"}
                    </div>
                  </div>
                </div>
                <div
                  className={`text-2xl font-black ${
                    item.status === "approved"
                      ? "text-emerald-600"
                      : item.status === "partial"
                        ? "text-amber-500"
                        : "text-red-500"
                  }`}
                >
                  {item.points > 0 ? `+${item.points}` : item.points}
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );

  // --- SEZIONE 3: ALTRE COMPETIZIONI (Layout Bozzetto) ---
  const CompetizioniSection = ({ full = false }) => (
    <div
      className={`space-y-8 ${full ? "max-w-3xl mx-auto animate-in fade-in slide-in-from-right-4 duration-500" : ""}`}
    >
      <div className="flex items-center gap-3">
        <h2 className="text-3xl font-black flex items-center gap-3 flex-1">
          Altre competizioni <span className="text-2xl opacity-50">📊</span>
        </h2>
        <button
          onClick={() => loadCompetitions()}
          disabled={isLoadingCompetitions}
          title="Ricarica"
          className="p-2 rounded-xl bg-stone-100 hover:bg-emerald-50 hover:text-emerald-700 disabled:opacity-40 transition text-xl leading-none shrink-0"
        >
          <span
            className={isLoadingCompetitions ? "inline-block animate-spin" : ""}
          >
            ↻
          </span>
        </button>
      </div>
      <Card className="border-2">
        <div className="text-xs font-black text-stone-400 uppercase tracking-[0.2em] block mb-3">
          Filtra competizioni attive
        </div>
        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-3"
          ref={filtersContainerRef}
        >
          <div className="relative">
            <button
              type="button"
              onClick={() =>
                setOpenFilterMenu((current) =>
                  current === "school" ? null : "school",
                )
              }
              className="w-full bg-stone-50 border-2 border-stone-200 rounded-xl px-4 py-3 pr-11 font-semibold text-slate-700 shadow-sm transition focus:outline-none focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100 hover:border-stone-300 text-left"
            >
              {selectedSchoolFilterLabel}
            </button>
            <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-stone-500">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className={`h-5 w-5 transition-transform ${openFilterMenu === "school" ? "rotate-180" : ""}`}
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.168l3.71-3.938a.75.75 0 1 1 1.08 1.04l-4.25 4.51a.75.75 0 0 1-1.08 0l-4.25-4.51a.75.75 0 0 1 .02-1.06Z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
            {openFilterMenu === "school" && (
              <div className="absolute z-20 mt-2 w-full bg-white border-2 border-stone-200 rounded-xl shadow-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedSchoolFilter("all");
                    setOpenFilterMenu(null);
                  }}
                  className={`w-full px-4 py-3 text-left font-semibold transition ${selectedSchoolFilter === "all" ? "bg-emerald-50 text-emerald-700" : "text-slate-700 hover:bg-stone-50"}`}
                >
                  Tutte le scuole
                </button>
                {schoolFilterOptions.map((school) => (
                  <button
                    key={school}
                    type="button"
                    onClick={() => {
                      setSelectedSchoolFilter(school);
                      setOpenFilterMenu(null);
                    }}
                    className={`w-full px-4 py-3 text-left font-semibold transition ${selectedSchoolFilter === school ? "bg-emerald-50 text-emerald-700" : "text-slate-700 hover:bg-stone-50"}`}
                  >
                    {school}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="relative">
            <button
              type="button"
              onClick={() =>
                setOpenFilterMenu((current) =>
                  current === "year" ? null : "year",
                )
              }
              className="w-full bg-stone-50 border-2 border-stone-200 rounded-xl px-4 py-3 pr-11 font-semibold text-slate-700 shadow-sm transition focus:outline-none focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100 hover:border-stone-300 text-left"
            >
              {selectedSchoolYearFilterLabel}
            </button>
            <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-stone-500">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className={`h-5 w-5 transition-transform ${openFilterMenu === "year" ? "rotate-180" : ""}`}
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.168l3.71-3.938a.75.75 0 1 1 1.08 1.04l-4.25 4.51a.75.75 0 0 1-1.08 0l-4.25-4.51a.75.75 0 0 1 .02-1.06Z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
            {openFilterMenu === "year" && (
              <div className="absolute z-20 mt-2 w-full bg-white border-2 border-stone-200 rounded-xl shadow-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedSchoolYearFilter("all");
                    setOpenFilterMenu(null);
                  }}
                  className={`w-full px-4 py-3 text-left font-semibold transition ${selectedSchoolYearFilter === "all" ? "bg-emerald-50 text-emerald-700" : "text-slate-700 hover:bg-stone-50"}`}
                >
                  Tutti gli anni
                </button>
                {schoolYearFilterOptions.map((schoolYear) => (
                  <button
                    key={schoolYear}
                    type="button"
                    onClick={() => {
                      setSelectedSchoolYearFilter(schoolYear);
                      setOpenFilterMenu(null);
                    }}
                    className={`w-full px-4 py-3 text-left font-semibold transition ${selectedSchoolYearFilter === schoolYear ? "bg-emerald-50 text-emerald-700" : "text-slate-700 hover:bg-stone-50"}`}
                  >
                    {schoolYear}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="relative">
            <button
              type="button"
              onClick={() =>
                setOpenFilterMenu((current) =>
                  current === "class" ? null : "class",
                )
              }
              className="w-full bg-stone-50 border-2 border-stone-200 rounded-xl px-4 py-3 pr-11 font-semibold text-slate-700 shadow-sm transition focus:outline-none focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100 hover:border-stone-300 text-left"
            >
              {selectedClassFilterLabel}
            </button>
            <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-stone-500">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className={`h-5 w-5 transition-transform ${openFilterMenu === "class" ? "rotate-180" : ""}`}
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.168l3.71-3.938a.75.75 0 1 1 1.08 1.04l-4.25 4.51a.75.75 0 0 1-1.08 0l-4.25-4.51a.75.75 0 0 1 .02-1.06Z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
            {openFilterMenu === "class" && (
              <div className="absolute z-20 mt-2 w-full bg-white border-2 border-stone-200 rounded-xl shadow-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedClassFilter("all");
                    setOpenFilterMenu(null);
                  }}
                  className={`w-full px-4 py-3 text-left font-semibold transition ${selectedClassFilter === "all" ? "bg-emerald-50 text-emerald-700" : "text-slate-700 hover:bg-stone-50"}`}
                >
                  Tutte le classi
                </button>
                {classFilterOptions.map((className) => (
                  <button
                    key={className}
                    type="button"
                    onClick={() => {
                      setSelectedClassFilter(className);
                      setOpenFilterMenu(null);
                    }}
                    className={`w-full px-4 py-3 text-left font-semibold transition ${selectedClassFilter === className ? "bg-emerald-50 text-emerald-700" : "text-slate-700 hover:bg-stone-50"}`}
                  >
                    {className}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </Card>
      <div className="flex flex-col gap-1">
        <div className="text-stone-400 font-black text-xs uppercase tracking-[0.2em]">
          a.s. 25/26
        </div>
        <div className="text-emerald-600 font-black text-xs uppercase underline decoration-2 underline-offset-4 decoration-emerald-200">
          COMPETIZIONI ATTIVE GESTITE DALL'AMMINISTRATORE
        </div>
      </div>

      {isLoadingCompetitions && (
        <Card className="border-2">
          <div className="text-stone-500 font-medium">
            Caricamento competizioni...
          </div>
        </Card>
      )}

      {competitionsError && (
        <Card className="border-2 border-red-200 bg-red-50">
          <div className="text-red-600 font-medium">{competitionsError}</div>
        </Card>
      )}

      {!isLoadingCompetitions &&
        !competitionsError &&
        visibleCompetitions.length === 0 && (
          <Card className="border-2">
            <div className="text-stone-500 font-medium">
              Non ci sono altre competizioni in corso.
            </div>
          </Card>
        )}

      {!isLoadingCompetitions &&
        !competitionsError &&
        visibleCompetitions.length > 0 &&
        filteredCompetitions.length === 0 && (
          <Card className="border-2">
            <div className="text-stone-500 font-medium">
              Nessuna competizione trovata con i filtri selezionati.
            </div>
          </Card>
        )}

      {filteredCompetitions.length > 0 && (
        <div className="space-y-4 pr-2">
          {filteredCompetitions.map((competition) => (
            <div
              key={competition.id}
              className={`bg-white border-2 rounded-2xl p-5 cursor-pointer transition ${selectedCompetitionId === competition.id ? "border-emerald-300" : "border-stone-100 hover:border-emerald-200"}`}
              onClick={() => setSelectedCompetitionId(competition.id)}
            >
              <div className="font-black text-slate-700 text-lg">
                {competition.name}
              </div>
              <div className="text-sm text-stone-500 mt-1">
                {competition.startDate.toLocaleDateString("it-IT")} -{" "}
                {competition.endDate.toLocaleDateString("it-IT")}
              </div>
              <div className="text-sm text-stone-500">
                Classi partecipanti: {competition.classes.length}
              </div>

              {selectedCompetitionId === competition.id && (
                <div className="mt-4 pt-4 border-t border-stone-200 space-y-4">
                  <div className="font-black text-slate-700">
                    Dettagli competizione
                  </div>
                  {competition.description && (
                    <div className="text-sm text-stone-600">
                      {competition.description}
                    </div>
                  )}
                  <div className="text-sm text-stone-500">
                    Periodo: {competition.startDate.toLocaleDateString("it-IT")}{" "}
                    - {competition.endDate.toLocaleDateString("it-IT")}
                  </div>

                  <div>
                    <div className="text-xs font-black text-stone-400 uppercase tracking-widest mb-2">
                      Classi partecipanti
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {competition.classes.map((className) => (
                        <span
                          key={`${competition.id}-${className}`}
                          className="bg-stone-100 text-stone-700 px-3 py-1 rounded-full text-sm font-medium"
                        >
                          {className}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs font-black text-stone-400 uppercase tracking-widest mb-2">
                      Tipi missioni eseguibili
                    </div>
                    {!competition.missionTypes ||
                    competition.missionTypes.length === 0 ? (
                      <div className="text-stone-500 font-medium">
                        Nessun tipo missione definito per questa competizione.
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {competition.missionTypes.map((missionType) => (
                          <span
                            key={`${competition.id}-${missionType}`}
                            className="bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full text-sm font-semibold"
                          >
                            {missionType}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {selectedCompetitionId &&
        !filteredCompetitions.find((c) => c.id === selectedCompetitionId) && (
          <></>
        )}
    </div>
  );

  return (
    <div className="min-h-[100dvh] lg:h-[100dvh] bg-stone-50 flex flex-col lg:flex-row lg:overflow-hidden">
      {/* Sidebar Desktop - Allineata al menu della landing */}
      <aside className="hidden lg:flex flex-col w-80 bg-white border-r border-stone-200 p-8 h-[100dvh] sticky top-0 overflow-hidden shadow-lg z-50">
        <button
          onClick={onGoToLanding}
          className="text-2xl font-black text-emerald-600 mb-6 tracking-tighter flex items-center gap-2 hover:opacity-80 transition cursor-pointer"
        >
          <span className="text-3xl">🌿</span> GreenHero
        </button>

        <div className="flex-1 min-h-0 flex flex-col pb-6">
          <nav className="space-y-2">
            <div className="text-[10px] font-black text-stone-300 uppercase tracking-[0.25em] pl-2 pb-1">
              DASHBOARD
            </div>

            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id as DashboardTab)}
                className={`w-full p-3 rounded-2xl font-black text-left flex items-center gap-4 transition ${activeTab === item.id ? "text-emerald-700 bg-emerald-50" : "text-stone-500 bg-stone-50 hover:bg-emerald-50 hover:text-emerald-700"}`}
              >
                <span className="text-xl">{item.icon}</span>
                {item.label}
              </button>
            ))}
          </nav>

          <div className="mt-auto pt-4 pb-2 border-t border-stone-100 space-y-2">
            <button
              onClick={() => setActiveTab("impostazioni")}
              className={`w-full p-3 rounded-2xl font-black text-left flex items-center gap-4 transition ${activeTab === "impostazioni" ? "text-emerald-700 bg-emerald-50" : "text-stone-500 bg-stone-50 hover:bg-emerald-50 hover:text-emerald-700"}`}
            >
              <span className="text-xl">⚙️</span> Impostazioni
            </button>
            <button
              onClick={onLogout}
              className="w-full p-3 rounded-2xl font-black text-left flex items-center gap-4 transition text-red-500 bg-red-50/60 hover:bg-red-50"
            >
              <span className="text-xl">🚪</span> Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Header Mobile */}
      <div className="lg:hidden bg-white border-b border-stone-200 p-5 sticky top-0 z-[60] flex justify-between items-center shadow-md">
        <button
          onClick={onGoToLanding}
          className="text-2xl font-black text-emerald-600 tracking-tighter hover:opacity-80 transition cursor-pointer"
        >
          🌿 GreenHero
        </button>
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-3 bg-stone-100 rounded-2xl active:scale-90 transition shadow-sm"
        >
          <svg
            className="w-6 h-6 text-slate-700"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>
      </div>

      {/* Menu Mobile - Stesso stile della landing */}
      {isMobileMenuOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 bg-stone-900/40 backdrop-blur-sm z-[60] transition-opacity duration-300"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div
            className="lg:hidden fixed top-0 left-0 h-full w-80 bg-white z-[70] shadow-2xl animate-in slide-in-from-left-4 duration-300 p-8 border-r border-stone-200 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-2xl font-black text-emerald-600 tracking-tighter flex items-center gap-2 cursor-pointer"
              >
                <span className="text-3xl">🌿</span> GreenHero
              </button>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 hover:bg-stone-100 rounded-full text-stone-400"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="flex-1 min-h-0 flex flex-col pb-6">
              <div className="space-y-2">
                <div className="text-[10px] font-black text-stone-300 uppercase tracking-[0.25em] pl-2 pb-1">
                  DASHBOARD
                </div>
                {menuItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setActiveTab(item.id as DashboardTab);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full p-3 rounded-2xl font-black text-left flex items-center gap-4 transition ${activeTab === item.id ? "text-emerald-700 bg-emerald-50" : "text-stone-500 bg-stone-50 hover:bg-emerald-50 hover:text-emerald-700"}`}
                  >
                    <span className="text-xl">{item.icon}</span> {item.label}
                  </button>
                ))}
              </div>

              <div className="mt-auto pt-4 pb-2 border-t border-stone-100 space-y-2">
                <button
                  onClick={() => {
                    setActiveTab("impostazioni");
                    setIsMobileMenuOpen(false);
                  }}
                  className={`w-full p-3 rounded-2xl font-black text-left flex items-center gap-4 transition ${activeTab === "impostazioni" ? "text-emerald-700 bg-emerald-50" : "text-stone-500 bg-stone-50 hover:bg-emerald-50 hover:text-emerald-700"}`}
                >
                  <span className="text-xl">⚙️</span> Impostazioni
                </button>
                <button
                  onClick={onLogout}
                  className="w-full p-3 rounded-2xl font-black text-left flex items-center gap-4 transition text-red-500 bg-red-50/60 hover:bg-red-50"
                >
                  <span className="text-xl">🚪</span> Logout
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Main Content Area */}
      <main className="flex-1 p-6 lg:p-14 lg:h-[100dvh] overflow-y-auto hide-scrollbar">
        {activeTab === "classe" &&
          (effectiveRole === "partecipante" || effectiveRole === "esterno") && (
            <ClasseSection full />
          )}
        {activeTab === "competizioni" && <CompetizioniSection full />}
        {activeTab === "validazioni" &&
          (effectiveRole === "esaminatore" ||
            effectiveRole === "amministratore") && <ValidazioniSection full />}
        {activeTab === "gestione-missioni" &&
          effectiveRole === "amministratore" &&
          GestioneMissioniSection({ full: true })}
        {activeTab === "gestione-classi" &&
          effectiveRole === "amministratore" &&
          GestioneClassiSection({ full: true })}
        {activeTab === "gestione-competizioni" &&
          effectiveRole === "amministratore" &&
          GestioneCompetizioniSection({ full: true })}
        {activeTab === "gestione-utenti" &&
          effectiveRole === "amministratore" && (
            <div className="max-w-5xl mx-auto space-y-8 animate-in fade-in duration-500">
              <div className="flex items-center gap-3">
                <h2 className="text-3xl font-black flex items-center gap-3 flex-1">
                  Gestione utenti{" "}
                  <span className="text-2xl opacity-50">👥</span>
                </h2>
                <button
                  onClick={() => loadManagedUsers()}
                  disabled={isLoadingManagedUsers}
                  title="Ricarica"
                  className="p-2 rounded-xl bg-stone-100 hover:bg-emerald-50 hover:text-emerald-700 disabled:opacity-40 transition text-xl leading-none shrink-0"
                >
                  <span
                    className={
                      isLoadingManagedUsers ? "inline-block animate-spin" : ""
                    }
                  >
                    ↻
                  </span>
                </button>
              </div>
              <Card className="border-2">
                <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
                  <StyledFilterDropdown
                    value={usersRoleFilter}
                    onChange={setUsersRoleFilter}
                    className="w-full md:w-64"
                    options={[
                      { value: "staff", label: "staff/tablet" },
                      { value: "tablet", label: "Tablet" },
                      { value: "esaminatore", label: "Esaminatore" },
                      { value: "amministratore", label: "Amministratore" },
                    ]}
                  />
                </div>

                <div className="mb-6 p-4 bg-stone-50 border border-stone-100 rounded-xl space-y-3">
                  <div className="text-xs font-black text-stone-400 uppercase tracking-widest">
                    Crea o cambia ruolo staff/tablet per email
                  </div>
                  <div className="flex flex-col md:flex-row gap-3">
                    <input
                      value={roleTargetEmail}
                      onChange={(event) =>
                        setRoleTargetEmail(event.target.value)
                      }
                      placeholder="email.account@scuola.it"
                      className="flex-1 bg-white border-2 border-stone-200 rounded-xl px-4 py-3 font-medium"
                    />
                    <StyledFilterDropdown
                      value={roleTargetValue}
                      onChange={setRoleTargetValue}
                      className="w-full md:w-56"
                      options={[
                        { value: "esaminatore", label: "Esaminatore" },
                        { value: "amministratore", label: "Amministratore" },
                        { value: "tablet", label: "Tablet" },
                      ]}
                    />
                    <button
                      type="button"
                      onClick={handleUpdateUserRoleByEmail}
                      disabled={isUpdatingRoleByEmail}
                      className="px-4 py-3 bg-emerald-100 text-emerald-700 rounded-xl font-bold text-sm hover:bg-emerald-200 transition disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isUpdatingRoleByEmail
                        ? "Salvataggio..."
                        : "Assegna ruolo"}
                    </button>
                  </div>
                </div>

                {managedUsersError && (
                  <div className="mb-4 text-sm font-bold text-red-600">
                    {managedUsersError}
                  </div>
                )}
                {managedUsersSuccess && (
                  <div className="mb-4 text-sm font-bold text-emerald-600">
                    {managedUsersSuccess}
                  </div>
                )}

                {isLoadingManagedUsers && (
                  <div className="mb-4 text-stone-500 font-medium">
                    Caricamento utenti...
                  </div>
                )}

                <div className="space-y-3">
                  {!isLoadingManagedUsers && managedUsers.length === 0 ? (
                    <div className="text-stone-500 font-medium">
                      Nessun utente trovato.
                    </div>
                  ) : (
                    managedUsers.map((managedUser) => (
                      <div
                        key={managedUser.id}
                        className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 bg-stone-50 p-4 rounded-xl"
                      >
                        <div>
                          <div className="font-black text-slate-700">
                            {managedUser.name}
                          </div>
                          <div className="text-sm text-stone-500">
                            {managedUser.email}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <StyledFilterDropdown
                            value={
                              userRoleDrafts[managedUser.id] ??
                              mapRoleToKnownValue(
                                String(managedUser.role ?? ""),
                              )
                            }
                            onChange={async (nextRole) => {
                              const currentRole =
                                userRoleDrafts[managedUser.id] ??
                                mapRoleToKnownValue(
                                  String(managedUser.role ?? ""),
                                );

                              setUserRoleDrafts((current) => ({
                                ...current,
                                [managedUser.id]: nextRole,
                              }));

                              if (nextRole === currentRole) {
                                return;
                              }

                              await handleUpdateUserRole(
                                managedUser.id,
                                nextRole,
                                currentRole,
                              );
                            }}
                            className="w-64"
                            disabled={updatingUserIds.includes(managedUser.id)}
                            options={[
                              {
                                value: "nessuno",
                                label: "Nessuno (privilegi base)",
                              },
                              { value: "esaminatore", label: "Esaminatore" },
                              {
                                value: "amministratore",
                                label: "Amministratore",
                              },
                              { value: "tablet", label: "Tablet" },
                            ]}
                          />
                          {updatingUserIds.includes(managedUser.id) && (
                            <span className="px-3 py-2 text-emerald-700 font-bold text-sm">
                              Salvataggio...
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </Card>
            </div>
          )}
        {activeTab === "storico" &&
          (effectiveRole === "partecipante" ||
            effectiveRole === "amministratore") && <StoricoSection full />}
        {activeTab === "impostazioni" && (
          <div className="max-w-2xl mx-auto space-y-12 animate-in fade-in zoom-in-95 duration-500">
            <h2 className="text-5xl font-black tracking-tighter">
              Impostazioni <span className="text-3xl opacity-30">⚙️</span>
            </h2>
            <Card className="p-10 border-2">
              <div className="space-y-10">
                <div className="flex items-center gap-8">
                  <div className="w-24 h-24 bg-stone-100 rounded-full flex items-center justify-center text-5xl border-4 border-white shadow-xl group cursor-pointer overflow-hidden">
                    <span className="group-hover:scale-125 transition-transform duration-500">
                      {effectiveRole === "amministratore"
                        ? "👑"
                        : effectiveRole === "esaminatore"
                          ? "👨‍🏫"
                          : "👤"}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-black text-slate-800">
                      {user.name}
                    </h3>
                    <p className="text-stone-400 font-bold uppercase text-[10px] tracking-widest mt-1">
                      {effectiveRole === "amministratore"
                        ? "AMMINISTRATORE"
                        : effectiveRole === "esaminatore"
                          ? "ESAMINATORE"
                          : effectiveRole === "partecipante"
                            ? "PARTECIPANTE"
                            : "UTENTE ESTERNO"}
                    </p>
                  </div>
                </div>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label
                        htmlFor="profile-name"
                        className="text-[10px] font-black uppercase text-stone-300 tracking-[0.2em] pl-1"
                      >
                        Nome
                      </label>
                      <input
                        id="profile-name"
                        name="profile_name"
                        disabled
                        value={user.name}
                        className="w-full bg-stone-50 border-2 border-stone-100 rounded-2xl px-5 py-4 font-bold text-slate-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <label
                        htmlFor="profile-email"
                        className="text-[10px] font-black uppercase text-stone-300 tracking-[0.2em] pl-1"
                      >
                        Email
                      </label>
                      <input
                        id="profile-email"
                        name="profile_email"
                        disabled
                        value={user.email}
                        className="w-full bg-stone-50 border-2 border-stone-100 rounded-2xl px-5 py-4 font-bold text-slate-400"
                      />
                    </div>
                  </div>
                  {user.class && (
                    <div className="space-y-2">
                      <label
                        htmlFor="profile-class"
                        className="text-[10px] font-black uppercase text-stone-300 tracking-[0.2em] pl-1"
                      >
                        Classe
                      </label>
                      <input
                        id="profile-class"
                        name="profile_class"
                        disabled
                        value={user.class}
                        className="w-full bg-stone-50 border-2 border-stone-100 rounded-2xl px-5 py-4 font-bold text-slate-400"
                      />
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </div>
        )}
      </main>

      {/* Ranking modal - livello Dashboard, visibile da qualsiasi tab */}
      {rankingModalCompetitionId && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{
            backgroundColor: "rgba(15,23,42,0.55)",
            backdropFilter: "blur(4px)",
          }}
          onClick={() => setRankingModalCompetitionId(null)}
        >
          <div
            className="bg-white rounded-3xl shadow-2xl border-2 border-stone-100 w-full max-w-lg max-h-[80dvh] flex flex-col overflow-hidden animate-in zoom-in-95 fade-in duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b-2 border-stone-100 shrink-0">
              <div>
                <div className="text-xl font-black text-slate-800">
                  🏆 Classifica
                </div>
                <div className="text-sm text-stone-500 font-medium mt-0.5">
                  {rankingModalName}
                </div>
              </div>
              <button
                onClick={() => setRankingModalCompetitionId(null)}
                className="p-2 bg-stone-100 hover:bg-stone-200 rounded-xl text-stone-500 transition"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {isLoadingRankingModal ? (
                <div className="text-center text-stone-500 font-bold py-8">
                  Caricamento...
                </div>
              ) : rankingModalData.length === 0 ? (
                <div className="text-center text-stone-400 font-medium py-8">
                  Nessun dato disponibile per questa competizione.
                </div>
              ) : (
                <div className="space-y-2">
                  {rankingModalData.map((row) => (
                    <div
                      key={row.classId}
                      className={`flex items-center justify-between px-4 py-3 rounded-2xl border-2 ${
                        row.position === 1
                          ? "bg-amber-50 border-amber-200"
                          : row.position === 2
                            ? "bg-stone-100 border-stone-200"
                            : row.position === 3
                              ? "bg-orange-50 border-orange-200"
                              : "bg-stone-50 border-stone-100"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xl">
                          {row.position === 1
                            ? "🥇"
                            : row.position === 2
                              ? "🥈"
                              : row.position === 3
                                ? "🥉"
                                : `#${row.position}`}
                        </span>
                        <span className="font-black text-slate-700">
                          {row.className}
                        </span>
                      </div>
                      <span className="font-black text-emerald-600 text-lg">
                        {row.score} pt
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- APP PRINCIPALE ---
const App: React.FC = () => {
  const [view, setView] = useState<View>(() => {
    try {
      const token = localStorage.getItem("gh_token");
      const saved = localStorage.getItem("gh_user");
      if (token && saved) {
        const u = JSON.parse(saved) as User;
        return u.role === "tablet" ? "tablet" : "dashboard";
      }
    } catch {}
    return "landing";
  });
  const [authToken, setAuthToken] = useState<string | null>(() =>
    localStorage.getItem("gh_token"),
  );
  const [currentUser, setCurrentUser] = useState<User | null>(() => {
    try {
      const saved = localStorage.getItem("gh_user");
      return saved ? (JSON.parse(saved) as User) : null;
    } catch {
      return null;
    }
  });
  const [initialDashboardTab, setInitialDashboardTab] =
    useState<DashboardTab>("competizioni");

  const handleLoginClick = () => setView("login");
  const handleBack = () => setView("landing");
  const handleLoginSuccess = (user: User, token?: string) => {
    const normalizedUser = normalizeUserRole(user);
    const defaultUserTab =
      getMenuItemsForRole(normalizedUser.role)[0]?.id ?? "competizioni";

    if (token) {
      localStorage.setItem("gh_token", token);
      setAuthToken(token);
    }
    localStorage.setItem("gh_user", JSON.stringify(normalizedUser));
    setCurrentUser(normalizedUser);
    setInitialDashboardTab(defaultUserTab);
    setView(normalizedUser.role === "tablet" ? "tablet" : "dashboard");
  };
  const handleGoToLanding = () => setView("landing");
  const handleOpenDashboard = (tab?: DashboardTab) => {
    if (!currentUser) {
      setView("login");
      return;
    }

    if (tab) {
      setInitialDashboardTab(tab);
    }

    setView(currentUser.role === "tablet" ? "tablet" : "dashboard");
  };
  const handleLogout = () => {
    localStorage.removeItem("gh_token");
    localStorage.removeItem("gh_user");
    setAuthToken(null);
    setCurrentUser(null);
    setView("landing");
  };
  const handleAuthError = () => {
    handleLogout();
    setView("login");
  };

  useEffect(() => {
    setAuthErrorHandler(handleAuthError);
  }, []);

  return (
    <div className="min-h-[100dvh] selection:bg-emerald-100 selection:text-emerald-900 font-sans antialiased text-slate-900 overflow-x-hidden">
      {view === "landing" && (
        <LandingPage
          onLoginClick={handleLoginClick}
          user={currentUser}
          onOpenDashboard={handleOpenDashboard}
          onLogout={handleLogout}
        />
      )}
      {view === "login" && (
        <LoginPage onBack={handleBack} onLogin={handleLoginSuccess} />
      )}
      {view === "dashboard" && currentUser && (
        <Dashboard
          onLogout={handleLogout}
          onGoToLanding={handleGoToLanding}
          user={currentUser}
          token={authToken}
          initialTab={initialDashboardTab}
        />
      )}
      {view === "tablet" && (
        <TabletInterface onLogout={handleLogout} token={authToken} />
      )}
    </div>
  );
};

export default App;
